import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Container, Spinner, Alert, Card, Button, Row, Col, Form, Table, Accordion, ListGroup, Modal, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faTrash, 
  faThumbsUp, 
  faThumbsDown, 
  faCheckDouble, 
  faTimes, 
  faExternalLinkAlt, 
  faList, 
  faClone, 
  faSync, 
  faCopy,
  faArrowLeft,
  faEye,
  faCalendarAlt,
  faPrint,
  faMinus,
  faSave,
  faCheck,
  faBan,
  faLightbulb,
  faCheckSquare,
  faSearch,
  faFilter,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { Event, EventEquipmentItem, FullEquipment, Category, Subcategory, EventSpecification, Location, Consumable, EventConsumableItem, EquipmentToEquipmentComplementaryItem, EquipmentToConsumableComplementaryItem, ClientToEquipmentComplementaryItem, ClientToConsumableComplementaryItem, EventColor } from '../interfaces';
import MessageModal from './MessageModal';
import ConfirmationModal from './ConfirmationModal';
import EquipmentDetailModal from './EquipmentDetailModal';
import EquipmentForm from './EquipmentForm';
import ConsumableForm from './ConsumableForm';
import _ from 'lodash';
import moment from 'moment';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL, buildApiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const formatCasesLabel = (quantity: number, caseQty?: number) => {
  const cq = Number(caseQty || 1);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(cq) || cq <= 1) return null;

  const rawCases = quantity / cq;
  const isInt = Number.isInteger(rawCases);
  const casesText = isInt ? String(rawCases) : rawCases.toFixed(2);
  const noun = rawCases === 1 ? 'קייס' : 'קייסים';
  return `${casesText} ${noun}`;
};

/** סינון לפי מקור: תומך גם בפריטים שמאחדים כמה סיבות ב־reason */
const recommendationReasonMatchesSource = (reason: string, sourceFilter: string): boolean => {
  if (!sourceFilter) return true;
  const r = reason || '';
  switch (sourceFilter) {
    case 'location':
      return r.includes('מומלץ למיקום');
    case 'client':
      return r.includes('מומלץ ללקוח');
    case 'complementary':
      return r.includes('ציוד משלים') || r.includes('מתכל משלים');
    case 'long_event':
      return r.includes('מומלץ לאירוע ארוך');
    case 'event_internal':
      return r.includes('מומלץ לאירוע פנימי');
    case 'event_external':
      return r.includes('מומלץ לאירוע חיצוני');
    case 'other':
      return (
        !r.includes('מומלץ למיקום') &&
        !r.includes('מומלץ ללקוח') &&
        !r.includes('ציוד משלים') &&
        !r.includes('מתכל משלים') &&
        !r.includes('מומלץ לאירוע ארוך') &&
        !r.includes('מומלץ לאירוע פנימי') &&
        !r.includes('מומלץ לאירוע חיצוני')
      );
    default:
      return true;
  }
};

const groupEventItems = (items: (EventEquipmentItem | EventConsumableItem)[], sortedCategories: string[]) => {
  const grouped: { [category: string]: { [subcategory: string]: (EventEquipmentItem | EventConsumableItem)[] } } = {};
  const categoriesWithRented = [...sortedCategories, 'מתכלים'];

  items.forEach(item => {
    let category = '';
    let subcategory = '';

    if ('equipment_name' in item) {
      if (item.is_rented_item) {
        // הציוד המושכר יוצג לפי הקטגוריה והתת-קטגוריה שלו
        category = item.rented_item_category || 'כללי';
        subcategory = item.rented_item_subcategory || 'כללי';
      } else {
        category = item.category_name || 'כללי';
        subcategory = item.subcategory_name || 'כללי';
      }
    } else if ('consumable_name' in item) {
      category = 'מתכלים';
      subcategory = 'כללי';
    } else {
      category = 'אחר';
      subcategory = 'אחר';
    }

    if (!grouped[category]) grouped[category] = {};
    if (!grouped[category][subcategory]) grouped[category][subcategory] = [];
    grouped[category][subcategory].push(item);
  });

  const sortedGrouped: { [category: string]: { [subcategory: string]: (EventEquipmentItem | EventConsumableItem)[] } } = {};
  categoriesWithRented.forEach(cat => {
    if (grouped[cat]) {
      sortedGrouped[cat] = grouped[cat];
    }
  });
  
  Object.keys(grouped).forEach(cat => {
      if (!categoriesWithRented.includes(cat)) {
          sortedGrouped[cat] = grouped[cat];
      }
  });

  return sortedGrouped;
};

const groupInventoryEquipment = (equipment: FullEquipment[], sortedCategories: string[]) => {
    const grouped: { [category: string]: { [subcategory: string]: FullEquipment[] } } = {};
    equipment.forEach(item => {
        const category = item.category_name || 'כללי';
        const subcategory = item.subcategory_name || 'כללי';
        if (!grouped[category]) grouped[category] = {};
        if (!grouped[category][subcategory]) grouped[category][subcategory] = [];
        grouped[category][subcategory].push(item);
    });
    
    const sortedGrouped: { [category: string]: { [subcategory: string]: FullEquipment[] } } = {};
    sortedCategories.forEach(cat => {
      if (grouped[cat]) {
        sortedGrouped[cat] = grouped[cat];
      }
    });

    return sortedGrouped;
};


interface EventEquipmentManagerProps {
  onBack: () => void;
  onBackToCalendar: () => void;
  onBackToDetails: (eventId: number) => void;
}

interface RecommendedItem {
    /** מפתח ייחודי (ציוד ומתכל יכולים לחלוק אותו מזהה מספרי) */
    dedupeKey: string;
    id: number;
    name: string;
    quantity: number;
    reason: string;
    reasons?: { text: string; quantity: number }[];
    isSelected: boolean;
    type: 'equipment' | 'consumable';
}

const STATUS_LABELS: { [key: string]: string } = {
    'planned': 'מתוכנן',
    'preparation': 'בהכנה',
    'active': 'פעיל/בחוץ',
    'completed': 'הסתיים',
    'cancelled': 'בוטל',
    'no_equipment_list': 'ללא רשימת ציוד',
};

const EventEquipmentManager: React.FC<EventEquipmentManagerProps> = ({ onBack, onBackToCalendar, onBackToDetails }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const eventIdNumber = parseInt(eventId || '0', 10);
  const isTechnician = user?.role?.name === 'TECHNICIAN';
  const canViewNoteAuthor = user?.role?.name === 'ADMIN';
  
  // State for event navigation
  const [events, setEvents] = useState<Event[]>([]);
  const [eventColors, setEventColors] = useState<EventColor[]>([]);
  const [loadingEvents, setLoadingEvents] = useState<boolean>(true);
  const [errorEvents, setErrorEvents] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<number | ''>(eventIdNumber || '');
  const selectedEvent = useMemo(() => events.find(e => e.id === selectedEventId), [events, selectedEventId]);

  // State for event search and filtering
  const [eventSearchTerm, setEventSearchTerm] = useState<string>('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');
  const [showEventFilters, setShowEventFilters] = useState<boolean>(false);
  
  const [event, setEvent] = useState<Event | null>(null);
  const [eventEquipment, setEventEquipment] = useState<EventEquipmentItem[]>([]);
  const [equipmentNotesDraft, setEquipmentNotesDraft] = useState<{ [key: number]: string }>({});
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const addQuantityTypingTimeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const [eventConsumables, setEventConsumables] = useState<EventConsumableItem[]>([]);
  const [equipmentAvailability, setEquipmentAvailability] = useState<any>(null);
  
  const [locations, setLocations] = useState<Location[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [allSubcategories, setAllSubcategories] = useState<Subcategory[]>([]);
  const [allSpecifications, setAllSpecifications] = useState<EventSpecification[]>([]);
  const [allInventoryEquipment, setAllInventoryEquipment] = useState<FullEquipment[]>([]);
  const [availableEquipmentMap, setAvailableEquipmentMap] = useState<Map<number, number>>(new Map());
  const [allConsumables, setAllConsumables] = useState<Consumable[]>([]);

  const [addMode, setAddMode] = useState<'internal' | 'external' | 'consumable'>('internal');
  const [quantitiesToAdd, setQuantitiesToAdd] = useState<{ [key: number]: string }>({});
  const [consumableQuantities, setConsumableQuantities] = useState<{ [key: number]: string }>({});

  const [rentedItemFormData, setRentedItemFormData] = useState({
    rented_item_name: '',
    rented_item_category_id: '' as number | '',
    rented_item_subcategory_id: '' as number | '',
    rental_supplier_name: '',
    rented_arrival_date: '',
    rented_departure_date: '',
    quantity: 1,
  });
  const [filteredSubcategories, setFilteredSubcategories] = useState<Subcategory[]>([]);
  const [selectedSpecificationToLoad, setSelectedSpecificationToLoad] = useState<number | ''>('');
  
  const [recommendedItems, setRecommendedItems] = useState<RecommendedItem[]>([]);
  const [recommendedItemsSearchTerm, setRecommendedItemsSearchTerm] = useState<string>('');
  const [recommendedItemsTypeFilter, setRecommendedItemsTypeFilter] = useState<string>('');
  const [recommendedItemsSourceFilter, setRecommendedItemsSourceFilter] = useState<string>('');

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalTitle, setMessageModalTitle] = useState<string>('');
  const [messageModalContent, setMessageModalContent] = useState<string>('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');

  const [showCopyFromEventModal, setShowCopyFromEventModal] = useState<boolean>(false);
  const [eventsToCopyFrom, setEventsToCopyFrom] = useState<Event[]>([]);
  const [selectedEventToCopyFrom, setSelectedEventToCopyFrom] = useState<number | ''>('');
  const [copyEventSearchQuery, setCopyEventSearchQuery] = useState<string>('');
  const [copiedItemsForSelection, setCopiedItemsForSelection] = useState<RecommendedItem[]>([]);

  // הוספת ציוד חדש למלאי (Modal שמופיע מעל העמוד)
  const [showAddNewEquipmentModal, setShowAddNewEquipmentModal] = useState<boolean>(false);
  // הוספת פריט מתכלה חדש (Modal שמופיע מעל העמוד)
  const [showAddNewConsumableModal, setShowAddNewConsumableModal] = useState<boolean>(false);

  const sortedCategories = useMemo(() => allCategories.sort((a, b) => a.category_name.localeCompare(b.category_name)).map(c => c.category_name), [allCategories]);
  const groupedEventItems = useMemo(() => groupEventItems([...eventEquipment, ...eventConsumables], sortedCategories), [eventEquipment, eventConsumables, sortedCategories]);
  const groupedInventory = useMemo(() => groupInventoryEquipment(allInventoryEquipment, sortedCategories), [allInventoryEquipment, sortedCategories]);

  // חיפוש ציוד
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [showSearchFilters, setShowSearchFilters] = useState<boolean>(false);
  
  // חיפוש ברשימת הפריטים שנבחרו
  const [selectedItemsSearchTerm, setSelectedItemsSearchTerm] = useState<string>('');
  const [selectedItemsSelectedCategory, setSelectedItemsSelectedCategory] = useState<string>('');
  const [selectedItemsSelectedSubcategory, setSelectedItemsSelectedSubcategory] = useState<string>('');
  const [showSelectedItemsSearchFilters, setShowSelectedItemsSearchFilters] = useState<boolean>(false);

  const filteredRecommendedItems = useMemo(() => {
    let filtered = [...recommendedItems];

    if (recommendedItemsSearchTerm) {
      const searchLower = recommendedItemsSearchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchLower) ||
        item.reason.toLowerCase().includes(searchLower)
      );
    }

    if (recommendedItemsTypeFilter) {
      filtered = filtered.filter(item => item.type === recommendedItemsTypeFilter);
    }

    if (recommendedItemsSourceFilter) {
      filtered = filtered.filter((item) =>
        recommendationReasonMatchesSource(item.reason, recommendedItemsSourceFilter)
      );
    }

    return filtered;
  }, [recommendedItems, recommendedItemsSearchTerm, recommendedItemsTypeFilter, recommendedItemsSourceFilter]);

  const areAllFilteredRecommendationsSelected = filteredRecommendedItems.length > 0 && filteredRecommendedItems.every(item => item.isSelected);

  const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning' = 'info') => {
    setMessageModalTitle(title);
    setMessageModalContent(message);
    setMessageModalVariant(variant);
    setShowMessageModal(true);
  };

  // Fetch all events for navigation (including completed, cancelled, and no_equipment_list)
  const fetchEvents = useCallback(async () => {
    setLoadingEvents(true);
    setErrorEvents(null);
    try {
      const [eventsRes, colorsRes] = await Promise.all([
        axios.get(buildApiUrl('events/events/')),
        axios.get(buildApiUrl('settings/event-colors/'))
      ]);
      
      setEvents(eventsRes.data);
      setEventColors(colorsRes.data);
    } catch (err) {
      setErrorEvents("לא ניתן לטעון את רשימת האירועים.");
      console.error("Error fetching events:", err);
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  // Effect to load events on component mount
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Filter events based on search and filter criteria
  const filteredEvents = useMemo(() => {
    let filtered = [...events];

    // Search filter
    if (eventSearchTerm) {
      const searchLower = eventSearchTerm.toLowerCase();
      filtered = filtered.filter(event => 
        event.event_name.toLowerCase().includes(searchLower) ||
        (event.client_name && event.client_name.toLowerCase().includes(searchLower)) ||
        (event.free_text_client && event.free_text_client.toLowerCase().includes(searchLower)) ||
        (event.location_name && event.location_name.toLowerCase().includes(searchLower)) ||
        (event.free_text_location && event.free_text_location.toLowerCase().includes(searchLower))
      );
    }

    // Status filter
    if (selectedStatusFilter) {
      filtered = filtered.filter(event => event.status === selectedStatusFilter);
    }

    // Date range filter
    if (startDateFilter) {
      filtered = filtered.filter(event => {
        const eventStartDate = new Date(event.start_date);
        const filterStartDate = new Date(startDateFilter);
        return eventStartDate >= filterStartDate;
      });
    }

    if (endDateFilter) {
      filtered = filtered.filter(event => {
        const eventStartDate = new Date(event.start_date);
        const filterEndDate = new Date(endDateFilter);
        return eventStartDate <= filterEndDate;
      });
    }

    return filtered;
  }, [events, eventSearchTerm, selectedStatusFilter, startDateFilter, endDateFilter]);

  // Clear all filters
  const clearEventFilters = () => {
    setEventSearchTerm('');
    setSelectedStatusFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
  };

  // Effect to handle event selection change
  useEffect(() => {
    if (selectedEventId) {
      // Update URL when event is selected
      navigate(`/manage-event-equipment/${selectedEventId}`, { replace: true });
      // Fetch data for the selected event
      fetchAllDependencies();
    } else {
      // If no event selected, clear data and go to general equipment manager
      setEvent(null);
      setEventEquipment([]);
      setEquipmentNotesDraft({});
      setEventConsumables([]);
      navigate(`/manage-event-equipment`, { replace: true });
    }
  }, [selectedEventId, navigate]);

  const fetchAllDependencies = useCallback(async () => {
    if (!selectedEventId) return;
    setLoading(true);
    setError(null);
    try {
      const [eventRes, equipmentRes, consumablesRes, locationsRes, categoriesRes, subcategoriesRes, specificationsRes, allEquipmentRes, allConsumablesRes, clientsRes, availabilityRes] = await Promise.all([
        axios.get(buildApiUrl(`events/events/${selectedEventId}/`)),
        axios.get(buildApiUrl(`events/event-equipment/?event=${selectedEventId}`)),
        axios.get(buildApiUrl(`events/event-consumables/?event=${selectedEventId}`)),
        axios.get(buildApiUrl('settings/locations/')),
        axios.get(buildApiUrl('settings/categories/')),
        axios.get(buildApiUrl('settings/subcategories/')),
        axios.get(buildApiUrl('events/event-specifications/')),
        axios.get(buildApiUrl('inventory/equipment/')),
        axios.get(buildApiUrl('settings/consumables/')),
        axios.get(buildApiUrl('settings/clients/')),
        axios.get(buildApiUrl(`events/events/${selectedEventId}/get-equipment-availability-status/`))
      ]);
      
      setEvent(eventRes.data);
      setEventEquipment(equipmentRes.data);
      const initialNotesDraft: { [key: number]: string } = {};
      equipmentRes.data.forEach((eq: EventEquipmentItem) => {
        if (eq.id !== undefined) {
          initialNotesDraft[Number(eq.id)] = eq.notes || '';
        }
      });
      setEquipmentNotesDraft(initialNotesDraft);
      setEventConsumables(consumablesRes.data);
      setEquipmentAvailability(availabilityRes.data);
      setLocations(locationsRes.data);
      setAllCategories(categoriesRes.data);
      setAllSubcategories(subcategoriesRes.data);
      setAllSpecifications(specificationsRes.data);
      setAllInventoryEquipment(allEquipmentRes.data);
      setAllConsumables(allConsumablesRes.data);
      setClients(clientsRes.data);
      
    } catch (err) {
      console.error("שגיאה בטעינת נתונים:", err);
      setError("לא ניתן לטעון את כל הנתונים הנדרשים. אנא רענן את הדף.");
    } finally {
      setLoading(false);
    }
  }, [selectedEventId]);

  const fetchAvailableEquipment = useCallback(async () => {
    if (!event?.start_date || !event?.end_date) return;
    try {
      const response = await axios.get(buildApiUrl('inventory/equipment/available/'), {
        params: {
          start_date: event.start_date,
          end_date: event.end_date,
          event_id: eventIdNumber,
          is_internal_event: event.is_internal,
        },
      });
      const availableMap = new Map<number, number>();
      response.data.forEach((eq: FullEquipment) => {
        if (eq.id) {
          availableMap.set(eq.id, eq.quantity);
        }
      });
      setAvailableEquipmentMap(availableMap);
    } catch (err) {
      console.error("שגיאה בטעינת ציוד זמין:", err);
    }
  }, [event, eventIdNumber]);

  useEffect(() => {
    fetchAvailableEquipment();
  }, [fetchAvailableEquipment]);

  const openAddNewEquipmentModal = () => {
    setShowAddNewEquipmentModal(true);
  };

  const openAddNewConsumableModal = () => {
    setShowAddNewConsumableModal(true);
  };
  
  useEffect(() => {
    if (rentedItemFormData.rented_item_category_id) {
      const relatedSubcategories = allSubcategories.filter(sub => sub.category === Number(rentedItemFormData.rented_item_category_id));
      setFilteredSubcategories(relatedSubcategories);
      if (!relatedSubcategories.some(s => s.id === rentedItemFormData.rented_item_subcategory_id)) {
        setRentedItemFormData(prev => ({ ...prev, rented_item_subcategory_id: '' }));
      }
    } else {
      setFilteredSubcategories([]);
      setRentedItemFormData(prev => ({ ...prev, rented_item_subcategory_id: '' }));
    }
  }, [rentedItemFormData.rented_item_category_id, allSubcategories]);

  const handleRentedItemChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setRentedItemFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddMultipleInternalItems = async () => {
    const itemsToProcess = Object.entries(quantitiesToAdd)
        .filter(([, qty]) => Number(qty) > 0)
        .map(([id, qty]) => ({
            equipmentId: Number(id),
            quantity: Number(qty),
        }));

    if (itemsToProcess.length === 0) {
        showMessage('מידע', 'לא נבחרו פריטים או כמויות להוספה.', 'info');
        return;
    }

    const itemsToUpdate: { id: number; quantity_needed: number }[] = [];
    const itemsToCreate: { event: number; equipment: number; quantity_needed: number; is_rented_item: boolean }[] = [];

    for (const item of itemsToProcess) {
        const availableQty = availableEquipmentMap.get(item.equipmentId) || 0;
        const existingItem = eventEquipment.find(eq => eq.equipment === item.equipmentId);
        const currentEventQty = existingItem ? existingItem.quantity_needed : 0;
        const newTotalQty = currentEventQty + item.quantity;
        
        if (newTotalQty > availableQty) {
            const itemName = allInventoryEquipment.find(eq => eq.id === item.equipmentId)?.name || 'פריט לא ידוע';
            showMessage('שגיאת מלאי', `לא ניתן להוסיף ${item.quantity} יחידות של ${itemName}. יש רק ${availableQty} זמינות לאירוע.`, 'danger');
            return;
        }
        
        if (existingItem) {
            itemsToUpdate.push({ id: existingItem.id as number, quantity_needed: newTotalQty });
        } else {
            itemsToCreate.push({ event: eventIdNumber, equipment: item.equipmentId, quantity_needed: newTotalQty, is_rented_item: false });
        }
    }

    try {
        const updatePromises = itemsToUpdate.map(item => axios.patch(buildApiUrl(`events/event-equipment/${item.id}/`), { quantity_needed: item.quantity_needed }));
        const createPromises = itemsToCreate.map(item => axios.post(buildApiUrl('events/event-equipment/'), item));
        
        await Promise.all([...updatePromises, ...createPromises]);

        // בניית פירוט השינויים
        const updatedDetails = itemsToUpdate.map(u => {
            const before = eventEquipment.find(eq => eq.id === u.id);
            const name = before?.equipment_name || before?.rented_item_name || `פריט #${u.id}`;
            const prevQty = before?.quantity_needed ?? 0;
            const newQty = u.quantity_needed;
            return `${name}: ${prevQty} → ${newQty}`;
        });
        const createdDetails = itemsToCreate.map(c => {
            const eqName = allInventoryEquipment.find(eq => eq.id === c.equipment)?.name || 'פריט חדש';
            return `${eqName}: ${c.quantity_needed}`;
        });
        const parts: string[] = [];
        if (updatedDetails.length) parts.push(`עודכנו כמויות (${updatedDetails.length}): ${updatedDetails.slice(0, 5).join(', ')}${updatedDetails.length > 5 ? ' ועוד...' : ''}`);
        if (createdDetails.length) parts.push(`נוספו פריטים (${createdDetails.length}): ${createdDetails.slice(0, 5).join(', ')}${createdDetails.length > 5 ? ' ועוד...' : ''}`);
        const summary = parts.length ? parts.join(' | ') : 'לא בוצעו שינויים.';

        showMessage('הצלחה', `רשימת הציוד עודכנה בהצלחה. ${summary}`, 'success');
        fetchAllDependencies();
        setQuantitiesToAdd({});
    } catch (err) {
        console.error("שגיאה בעדכון/הוספת ציוד:", err);
        showMessage('שגיאה', 'אירעה שגיאה בעת עדכון הציוד.', 'danger');
    }
  };

  const handleAddExternalEquipment = async () => {
    if (!rentedItemFormData.rented_item_name || !rentedItemFormData.rental_supplier_name || !rentedItemFormData.rented_item_category_id) {
      showMessage('שגיאה', "שם פריט, ספק וקטגוריה הם שדות חובה.", 'danger');
      return;
    }
    const selectedCategory = allCategories.find(c => c.id === Number(rentedItemFormData.rented_item_category_id));
    const selectedSubcategory = allSubcategories.find(s => s.id === Number(rentedItemFormData.rented_item_subcategory_id));

    const newItem = {
        event: eventIdNumber,
        equipment: null,
        quantity_needed: rentedItemFormData.quantity,
        is_rented_item: true,
        rented_item_name: rentedItemFormData.rented_item_name,
        rental_supplier_name: rentedItemFormData.rental_supplier_name,
        rented_item_category: selectedCategory?.category_name,
        rented_item_subcategory: selectedSubcategory?.subcategory_name,
        rented_arrival_date: rentedItemFormData.rented_arrival_date || null,
        rented_departure_date: rentedItemFormData.rented_departure_date || null,
    };
    try {
        await axios.post(buildApiUrl('events/event-equipment/'), newItem);
        showMessage('הצלחה', `הציוד המושכר "${newItem.rented_item_name}" (${newItem.quantity_needed} יח') נוסף לאירוע בהצלחה!`, 'success');
        fetchAllDependencies();
        setRentedItemFormData({ rented_item_name: '', rented_item_category_id: '', rented_item_subcategory_id: '', rental_supplier_name: '', rented_arrival_date: '', rented_departure_date: '', quantity: 1 });
    } catch (err) {
        console.error("שגיאה בהוספת ציוד מושכר:", err);
        showMessage('שגיאה', 'שגיאה בהוספת הציוד המושכר.', 'danger');
    }
  };

  const handleRemoveItem = (itemId: number, type: 'equipment' | 'consumable') => {
    setConfirmModalTitle('אישור הסרה');
    setConfirmModalMessage('האם אתה בטוח שברצונך להסיר פריט זה מהאירוע?');
    setConfirmAction(() => async () => {
        const url = type === 'equipment' 
            ? buildApiUrl(`events/event-equipment/${itemId}/`)
            : buildApiUrl(`events/event-consumables/${itemId}/`);
        try {
            await axios.delete(url);
            const removedEquipment = type === 'equipment' ? eventEquipment.find(e => e.id === itemId) : eventConsumables.find(c => c.id === itemId);
            let removedName = '';
            if (removedEquipment) {
                const rem: any = removedEquipment;
                removedName = rem.equipment_name || rem.rented_item_name || rem.consumable_name || '';
            }
            showMessage('הצלחה', removedName ? `הפריט "${removedName}" הוסר מהאירוע בהצלחה!` : 'הפריט הוסר מהאירוע בהצלחה!', 'success');
            fetchAllDependencies();
        } catch (err) {
            console.error("שגיאה בהסרת פריט:", err);
            showMessage('שגיאה', 'שגיאה בהסרת הפריט מהאירוע.', 'danger');
        }
    });
    setShowConfirmModal(true);
  };
  
  const handleSaveConsumables = async () => {
    const itemsToProcess = Object.entries(consumableQuantities)
        .filter(([, qty]) => Number(qty) > 0)
        .map(([id, qty]) => ({
            consumableId: Number(id),
            quantity: Number(qty),
        }));

    const itemsToUpdate: any[] = [];
    const itemsToCreate: any[] = [];
    const itemsToDelete: number[] = [];

    const existingItemsMap = new Map(eventConsumables.map(item => [item.consumable, item]));

    allConsumables.forEach(consumable => {
        const existingItem = existingItemsMap.get(consumable.id!);
        const newItemData = itemsToProcess.find(item => item.consumableId === consumable.id);
        const newQuantity = newItemData ? Number(newItemData.quantity) : 0;
        const existingQuantity = existingItem ? Number(existingItem.quantity_used) : 0;
        const totalQuantity = existingQuantity + newQuantity;

        if (existingItem) {
            if (totalQuantity > 0) {
                itemsToUpdate.push({ id: existingItem.id, quantity_used: totalQuantity });
            } else {
                itemsToDelete.push(existingItem.id);
            }
        } else if (newQuantity > 0) {
            if (consumable.id) {
                itemsToCreate.push({ event: eventIdNumber, consumable: consumable.id, quantity_used: newQuantity });
            }
        }
    });

    try {
        const updatePromises = itemsToUpdate.map(item => axios.patch(buildApiUrl(`events/event-consumables/${item.id}/`), { quantity_used: item.quantity_used }));
        const createPromises = itemsToCreate.map(item => axios.post(buildApiUrl('events/event-consumables/'), { ...item, event: eventIdNumber }));
        const deletePromises = itemsToDelete.map(id => axios.delete(buildApiUrl(`events/event-consumables/${id}/`)));
        
        await Promise.all([...updatePromises, ...createPromises, ...deletePromises]);

        // פירוט שינויים במתכלים
        const nameById = new Map(allConsumables.map(c => [c.id!, c.name]));
        const updatedDetails = itemsToUpdate.map(u => {
            const before = eventConsumables.find(ec => ec.id === u.id);
            const name = before?.consumable_name || (before?.consumable ? nameById.get(before.consumable) : undefined) || `פריט #${u.id}`;
            const prevQty = before?.quantity_used ?? 0;
            return `${name}: ${prevQty} → ${u.quantity_used}`;
        });
        const createdDetails = itemsToCreate.map(c => `${nameById.get(c.consumable) || 'פריט חדש'}: ${c.quantity_used}`);
        const deletedDetails = itemsToDelete.map(id => {
            const before = eventConsumables.find(ec => ec.id === id);
            const name = before?.consumable_name || (before?.consumable ? nameById.get(before.consumable) : undefined) || `פריט #${id}`;
            return `${name}`;
        });
        const parts: string[] = [];
        if (updatedDetails.length) parts.push(`עודכנו כמויות (${updatedDetails.length}): ${updatedDetails.slice(0, 5).join(', ')}${updatedDetails.length > 5 ? ' ועוד...' : ''}`);
        if (createdDetails.length) parts.push(`נוספו פריטים (${createdDetails.length}): ${createdDetails.slice(0, 5).join(', ')}${createdDetails.length > 5 ? ' ועוד...' : ''}`);
        if (deletedDetails.length) parts.push(`נמחקו פריטים (${deletedDetails.length}): ${deletedDetails.slice(0, 5).join(', ')}${deletedDetails.length > 5 ? ' ועוד...' : ''}`);
        const summary = parts.length ? parts.join(' | ') : 'לא בוצעו שינויים.';

        showMessage('הצלחה', `רשימת המתכלים עודכנה בהצלחה. ${summary}`, 'success');
        fetchAllDependencies();
        setConsumableQuantities({});
    } catch (err) {
        console.error("שגיאה בעדכון מתכלים:", err);
        if (axios.isAxiosError(err) && err.response && err.response.data) {
            let errorMessages = '';
            for (const key in err.response.data) {
                if (Object.prototype.hasOwnProperty.call(err.response.data, key)) {
                    const errorValue = err.response.data[key];
                    if (Array.isArray(errorValue)) {
                        errorMessages += `${key}: ${errorValue.join(', ')}\n`;
                    } else {
                        errorMessages += `${key}: ${errorValue}\n`;
                    }
                }
            }
            showMessage('שגיאה', `שגיאה בשמירת המתכלים: \n${errorMessages || 'שגיאה לא ידועה.'}`, 'danger');
        } else {
            showMessage('שגיאה', 'אירעה שגיאה בעת עדכון המתכלים. אנא נסה שוב.', 'danger');
        }
    }
  };
  
  const handleRecommend = async () => {
    if (!event) return;

    type RecEntry = {
      quantity: number;
      reasons: { text: string; quantity: number }[];
      type: 'equipment' | 'consumable';
    };
    const allRecommendations = new Map<string, RecEntry>();

    const mergeRec = (key: string, typ: 'equipment' | 'consumable', qty: number, reasonText: string) => {
      const current = allRecommendations.get(key) || { quantity: 0, reasons: [], type: typ };
      allRecommendations.set(key, {
        quantity: current.quantity + qty,
        reasons: [...current.reasons, { text: reasonText, quantity: qty }],
        type: typ,
      });
    };

    if (event.location) {
      const eventLocation = locations.find((loc) => loc.id === event.location);
      if (eventLocation) {
        eventLocation.recommended_equipment_details?.forEach((item) => {
          if (item.equipment) {
            mergeRec(`e:${item.equipment}`, 'equipment', item.quantity, `מומלץ למיקום "${eventLocation.location_name}"`);
          }
        });
        eventLocation.recommended_consumable_details?.forEach((item) => {
          if (item.consumable) {
            mergeRec(`c:${item.consumable}`, 'consumable', item.quantity, `מומלץ למיקום "${eventLocation.location_name}"`);
          }
        });
      }
    }

    if (event.client) {
      const eventClient = clients.find((c) => c.id === event.client);
      if (eventClient) {
        eventClient.recommended_equipment_details?.forEach((item: ClientToEquipmentComplementaryItem) => {
          if (item.equipment) {
            mergeRec(`e:${item.equipment}`, 'equipment', item.quantity, `מומלץ ללקוח "${eventClient.client_name}"`);
          }
        });
        eventClient.recommended_consumable_details?.forEach((item: ClientToConsumableComplementaryItem) => {
          if (item.consumable) {
            mergeRec(`c:${item.consumable}`, 'consumable', item.quantity, `מומלץ ללקוח "${eventClient.client_name}"`);
          }
        });
      }
    }

    eventEquipment.forEach((eventItem) => {
      if (eventItem.equipment) {
        const mainEquip = allInventoryEquipment.find((eq) => eq.id === eventItem.equipment);
        if (mainEquip?.complementary_items) {
          mainEquip.complementary_items.forEach((compItem: EquipmentToEquipmentComplementaryItem) => {
            if (compItem.complementary_equipment) {
              const recommendationQuantity = compItem.quantity * eventItem.quantity_needed;
              mergeRec(
                `e:${compItem.complementary_equipment}`,
                'equipment',
                recommendationQuantity,
                `ציוד משלים עבור "${mainEquip.name}"`
              );
            }
          });
        }
        if (mainEquip?.complementary_consumable_items) {
          mainEquip.complementary_consumable_items.forEach((row: EquipmentToConsumableComplementaryItem) => {
            if (row.consumable) {
              const recommendationQuantity = row.quantity * eventItem.quantity_needed;
              mergeRec(
                `c:${row.consumable}`,
                'consumable',
                recommendationQuantity,
                `מתכל משלים עבור "${mainEquip.name}"`
              );
            }
          });
        }
      }
    });

    try {
      const [longEventRes, internalExternalRes, longConsRes, internalExternalConsRes] = await Promise.all([
        axios.get<{ equipment: number; quantity: number }[]>(buildApiUrl('complementary-equipment/long-event/')),
        axios.get<{ equipment: number; quantity: number; is_internal: boolean }[]>(
          buildApiUrl('complementary-equipment/internal-external-event/')
        ),
        axios.get<{ consumable: number; quantity: number }[]>(buildApiUrl('complementary-equipment/long-event-consumable/')),
        axios.get<{ consumable: number; quantity: number; is_internal: boolean }[]>(
          buildApiUrl('complementary-equipment/internal-external-event-consumable/')
        ),
      ]);

      const eventDurationHours = moment(event.end_date).diff(moment(event.start_date), 'hours', true);
      const isLongEvent = eventDurationHours > 24;

      if (isLongEvent) {
        longEventRes.data.forEach((row) => {
          if (!row.equipment || row.quantity <= 0) return;
          mergeRec(`e:${row.equipment}`, 'equipment', row.quantity, 'מומלץ לאירוע ארוך');
        });
        longConsRes.data.forEach((row) => {
          if (!row.consumable || row.quantity <= 0) return;
          mergeRec(`c:${row.consumable}`, 'consumable', row.quantity, 'מומלץ לאירוע ארוך');
        });
      }

      internalExternalRes.data.forEach((row) => {
        if (!row.equipment || row.quantity <= 0) return;
        const matchesEventType =
          (event.is_internal && row.is_internal) || (!event.is_internal && !row.is_internal);
        if (!matchesEventType) return;
        const label = row.is_internal ? 'מומלץ לאירוע פנימי' : 'מומלץ לאירוע חיצוני';
        mergeRec(`e:${row.equipment}`, 'equipment', row.quantity, label);
      });

      internalExternalConsRes.data.forEach((row) => {
        if (!row.consumable || row.quantity <= 0) return;
        const matchesEventType =
          (event.is_internal && row.is_internal) || (!event.is_internal && !row.is_internal);
        if (!matchesEventType) return;
        const label = row.is_internal ? 'מומלץ לאירוע פנימי' : 'מומלץ לאירוע חיצוני';
        mergeRec(`c:${row.consumable}`, 'consumable', row.quantity, label);
      });
    } catch (err) {
      console.error('שגיאה בטעינת המלצות אירוע (ארוך / פנימי-חיצוני):', err);
    }

    if (event.description) {
      const sections = event.description.split('---SPEC_DELIMITER---');
      if (sections.length === 5) {
        // הוסף לוגיקה לזיהוי המלצות לפי מפרט כאן אם רלוונטי
      }
    }

    if (allRecommendations.size > 0) {
      const newRecommendations: RecommendedItem[] = Array.from(allRecommendations.entries()).map(([key, data]) => {
        const id = parseInt(key.slice(2), 10);
        const reasonText = data.reasons.map((r) => `${r.text} (+${r.quantity})`).join(', ');
        if (data.type === 'equipment') {
          const eqItem = allInventoryEquipment.find((eq) => eq.id === id);
          return {
            dedupeKey: key,
            id,
            name: eqItem?.name || `ציוד #${id}`,
            quantity: data.quantity,
            reason: reasonText,
            reasons: data.reasons,
            isSelected: false,
            type: 'equipment',
          };
        }
        const cons = allConsumables.find((c) => c.id === id);
        return {
          dedupeKey: key,
          id,
          name: cons?.name || `מתכלה #${id}`,
          quantity: data.quantity,
          reason: reasonText,
          reasons: data.reasons,
          isSelected: false,
          type: 'consumable',
        };
      });
      setRecommendedItems(newRecommendations);
      setRecommendedItemsSearchTerm('');
      setRecommendedItemsTypeFilter('');
      setRecommendedItemsSourceFilter('');
    } else {
      showMessage(
        'אין המלצות',
        'לא נמצאו פריטי ציוד או מתכלים מומלצים על בסיס הנתונים הנוכחיים.',
        'info'
      );
    }
  };

  const handleRecommendSelection = async (action: 'accept_all' | 'reject_all' | 'accept_selected') => {
    if (action === 'reject_all') {
      setRecommendedItems([]);
      return;
    }

    let itemsToProcess = [];
    if (action === 'accept_all') {
      itemsToProcess = recommendedItems;
    } else {
      itemsToProcess = recommendedItems.filter(item => item.isSelected);
    }
    
    if (itemsToProcess.length === 0) {
        showMessage('מידע', 'לא נבחרו פריטים להמלצות.', 'info');
        return;
    }

    const equipmentToUpdate: { id: number; quantity_needed: number }[] = [];
    const equipmentToCreate: { event: number; equipment: number | null; quantity_needed: number; is_rented_item: boolean; rented_item_name?: string }[] = [];
    const consumablesToUpdate: { id: number; quantity_used: number }[] = [];
    const consumablesToCreate: { event: number; consumable: number; quantity_used: number }[] = [];

    for (const item of itemsToProcess) {
        if (item.type === 'equipment') {
            const isRented = item.reason === 'מושכר';
            const existingItem = eventEquipment.find(eq => (isRented ? eq.rented_item_name === item.name : eq.equipment === item.id) && eq.is_rented_item === isRented);

            if (!isRented) {
                const availableQty = availableEquipmentMap.get(item.id) || 0;
                const currentEventQty = existingItem ? existingItem.quantity_needed : 0;
                const newTotalQty = currentEventQty + item.quantity;
                if (newTotalQty > availableQty) {
                    const itemName = allInventoryEquipment.find(eq => eq.id === item.id)?.name || 'פריט לא ידוע';
                    showMessage('שגיאת מלאי', `לא ניתן להוסיף ${item.quantity} יחידות של ${itemName}. יש רק ${availableQty} זמינות לאירוע.`, 'danger');
                    return;
                }
            }

            if (existingItem) {
                const newTotalQty = existingItem.quantity_needed + item.quantity;
                equipmentToUpdate.push({ id: existingItem.id as number, quantity_needed: newTotalQty });
            } else {
                if (isRented) {
                    equipmentToCreate.push({ event: eventIdNumber, equipment: null, quantity_needed: item.quantity, is_rented_item: true, rented_item_name: item.name });
                } else {
                    equipmentToCreate.push({ event: eventIdNumber, equipment: item.id, quantity_needed: item.quantity, is_rented_item: false });
                }
            }

        } else if (item.type === 'consumable') {
            const existingItem = eventConsumables.find(c => c.consumable === item.id);
            const currentEventQty = existingItem ? existingItem.quantity_used : 0;
            const newTotalQty = currentEventQty + item.quantity;

            if (existingItem) {
                consumablesToUpdate.push({ id: existingItem.id, quantity_used: newTotalQty });
            } else {
                consumablesToCreate.push({ event: eventIdNumber, consumable: item.id, quantity_used: newTotalQty });
            }
        }
    }

    try {
        await Promise.all([
            ...equipmentToUpdate.map(item => axios.patch(buildApiUrl(`events/event-equipment/${item.id}/`), { quantity_needed: item.quantity_needed })),
            ...equipmentToCreate.map(item => axios.post(buildApiUrl('events/event-equipment/'), item)),
            ...consumablesToUpdate.map(item => axios.patch(buildApiUrl(`events/event-consumables/${item.id}/`), { quantity_used: item.quantity_used })),
            ...consumablesToCreate.map(item => axios.post(buildApiUrl('events/event-consumables/'), item)),
        ]);
        showMessage('הצלחה', 'ההמלצות נשמרו והתווספו לאירוע בהצלחה!', 'success');
        setRecommendedItems([]);
        fetchAllDependencies();
        setCopiedItemsForSelection([]);
    } catch (err) {
        console.error("שגיאה בהוספת המלצות:", err);
        showMessage('שגיאה', 'אירעה שגיאה בעת שמירת ההמלצות.', 'danger');
    }
  };

  const [specificationItemsForSelection, setSpecificationItemsForSelection] = useState<RecommendedItem[]>([]);
  const [showSpecificationSelectionModal, setShowSpecificationSelectionModal] = useState<boolean>(false);
  
  // Modal לפרטי ציוד
  const [showEquipmentDetailModal, setShowEquipmentDetailModal] = useState<boolean>(false);
  const [selectedEquipmentForDetails, setSelectedEquipmentForDetails] = useState<FullEquipment | null>(null);

  const handleLoadSpecification = async () => {
    if (!selectedSpecificationToLoad) return;
    const spec = allSpecifications.find(s => s.id === Number(selectedSpecificationToLoad));
    if (!spec) {
        showMessage('שגיאה', 'מפרט לא תקין.', 'danger');
        return;
    }

    // יצירת רשימת פריטים לבחירה
    const items: RecommendedItem[] = [];

    // הוספת ציוד רגיל
    if (spec.items) {
        spec.items.forEach(specItem => {
            const itemName = allInventoryEquipment.find(eq => eq.id === specItem.equipment)?.name || 'פריט לא ידוע';
            items.push({
                dedupeKey: `eq-${specItem.equipment}`,
                id: specItem.equipment,
                name: itemName,
                quantity: specItem.quantity,
                reason: 'מפרט קבוע',
                reasons: undefined,
                isSelected: true,
                type: 'equipment'
            });
        });
    }

    // הוספת מתכלים
    if (spec.consumables) {
        spec.consumables.forEach(specItem => {
            const consumableName = allConsumables.find(c => c.id === specItem.consumable)?.name || 'מתכלה לא ידוע';
            items.push({
                dedupeKey: `co-${specItem.consumable}`,
                id: specItem.consumable,
                name: consumableName,
                quantity: specItem.quantity,
                reason: 'מפרט קבוע',
                reasons: undefined,
                isSelected: true,
                type: 'consumable'
            });
        });
    }

    // הוספת ציוד מושכר
    if (spec.rented_items) {
        spec.rented_items.forEach(specItem => {
            const rid = specItem.id ?? `name-${specItem.name}`;
            items.push({
                dedupeKey: `rent-${rid}`,
                id: typeof specItem.id === 'number' ? specItem.id : Math.floor(Math.random() * 1e9),
                name: specItem.name,
                quantity: specItem.quantity,
                reason: 'מפרט קבוע (מושכר)',
                reasons: undefined,
                isSelected: true,
                type: 'equipment'
            });
        });
    }

    if (items.length > 0) {
        setSpecificationItemsForSelection(items);
        setShowSpecificationSelectionModal(true);
    } else {
        showMessage('מידע', 'המפרט הנבחר אינו מכיל פריטים.', 'info');
    }
  };

  const handleSpecificationSelection = async (action: 'accept_all' | 'reject_all' | 'accept_selected') => {
    if (action === 'reject_all') {
        setSpecificationItemsForSelection([]);
        setShowSpecificationSelectionModal(false);
        return;
    }

    let itemsToProcess = [];
    if (action === 'accept_all') {
        itemsToProcess = specificationItemsForSelection;
    } else {
        itemsToProcess = specificationItemsForSelection.filter(item => item.isSelected);
    }
    
    if (itemsToProcess.length === 0) {
        showMessage('מידע', 'לא נבחרו פריטים מהמפרט.', 'info');
        return;
    }

    const equipmentToUpdate: { id: number; quantity_needed: number }[] = [];
    const equipmentToCreate: any[] = [];
    const consumablesToUpdate: { id: number; quantity_used: number }[] = [];
    const consumablesToCreate: any[] = [];
    
    let updatedMessage = '';
    const newItemsAdded: string[] = [];
    const existingItemsUpdated: string[] = [];

    for (const item of itemsToProcess) {
        if (item.type === 'equipment') {
            if (item.reason === 'מפרט קבוע (מושכר)') {
                // זה ציוד מושכר מהמפרט
                const spec = allSpecifications.find(s => s.id === Number(selectedSpecificationToLoad));
                const specItem = spec?.rented_items?.find(ri => ri.name === item.name);
                
                if (specItem) {
                    const existingItem = eventEquipment.find(eq => 
                        eq.is_rented_item && 
                        eq.rented_item_name === specItem.name && 
                        eq.rented_item_category === specItem.category_name
                    );
                    
                    if (existingItem) {
                        const newTotalQty = existingItem.quantity_needed + item.quantity;
                        equipmentToUpdate.push({ id: existingItem.id as number, quantity_needed: newTotalQty });
                        existingItemsUpdated.push(`${specItem.name} (מושכר, כמות: ${existingItem.quantity_needed} -> ${newTotalQty})`);
                    } else {
                        equipmentToCreate.push({
                            event: eventIdNumber,
                            equipment: null,
                            quantity_needed: item.quantity,
                            is_rented_item: true,
                            rented_item_name: specItem.name,
                            rented_item_category: specItem.category_name,
                            rented_item_subcategory: specItem.subcategory_name,
                            rental_supplier_name: specItem.supplier_name,
                            rented_arrival_date: specItem.arrival_date,
                            rented_departure_date: specItem.departure_date
                        });
                        newItemsAdded.push(`${specItem.name} (מושכר, כמות: ${item.quantity})`);
                    }
                }
            } else {
                // זה ציוד רגיל מהמלאי
                const availableQty = availableEquipmentMap.get(item.id) || 0;
                const existingItem = eventEquipment.find(eq => eq.equipment === item.id);
                const currentEventQty = existingItem ? existingItem.quantity_needed : 0;
                const newTotalQty = currentEventQty + item.quantity;
                const itemName = allInventoryEquipment.find(eq => eq.id === item.id)?.name || 'פריט לא ידוע';

                if (newTotalQty > availableQty) {
                    showMessage('שגיאת מלאי', `לא ניתן לטעון את המפרט. אין מספיק יחידות של ${itemName} זמינות.`, 'danger');
                    return;
                }

                if (existingItem) {
                    if (newTotalQty !== currentEventQty) {
                        equipmentToUpdate.push({ id: existingItem.id as number, quantity_needed: newTotalQty });
                        existingItemsUpdated.push(`${itemName} (כמות: ${currentEventQty} -> ${newTotalQty})`);
                    }
                } else {
                    equipmentToCreate.push({ event: eventIdNumber, equipment: item.id, quantity_needed: newTotalQty, is_rented_item: false });
                    newItemsAdded.push(`${itemName} (כמות: ${newTotalQty})`);
                }
            }
        } else if (item.type === 'consumable') {
            const existingItem = eventConsumables.find(c => c.consumable === item.id);
            const consumableName = allConsumables.find(c => c.id === item.id)?.name || 'פריט מתכלה לא ידוע';
            
            if (existingItem) {
                const newTotalQuantity = existingItem.quantity_used + item.quantity;
                if (newTotalQuantity !== existingItem.quantity_used) {
                    consumablesToUpdate.push({ id: existingItem.id, quantity_used: newTotalQuantity });
                    existingItemsUpdated.push(`${consumableName} (כמות: ${existingItem.quantity_used} -> ${newTotalQuantity})`);
                }
            } else {
                consumablesToCreate.push({ event: eventIdNumber, consumable: item.id, quantity_used: item.quantity });
                newItemsAdded.push(`${consumableName} (כמות: ${item.quantity})`);
            }
        }
    }
    
    try {
        const promises = [
            ...equipmentToUpdate.map(item => axios.patch(buildApiUrl(`events/event-equipment/${item.id}/`), { quantity_needed: item.quantity_needed })),
            ...equipmentToCreate.map(item => axios.post(buildApiUrl('events/event-equipment/'), item)),
            ...consumablesToUpdate.map(item => axios.patch(buildApiUrl(`events/event-consumables/${item.id}/`), { quantity_used: item.quantity_used })),
            ...consumablesToCreate.map(item => axios.post(buildApiUrl('events/event-consumables/'), item)),
        ];
        
        await Promise.all(promises);
        
        updatedMessage += newItemsAdded.length > 0 ? `נוספו פריטים חדשים: ${newItemsAdded.join(', ')}. ` : '';
        updatedMessage += existingItemsUpdated.length > 0 ? `עודכנו פריטים קיימים: ${existingItemsUpdated.join(', ')}. ` : '';
        
        if (updatedMessage) {
            showMessage('מפרט נטען בהצלחה', updatedMessage, 'success');
        } else {
            showMessage('מידע', 'המפרט נטען, אך לא היה צורך בעדכון או הוספת פריטים.', 'info');
        }

        setShowSpecificationSelectionModal(false);
        setSpecificationItemsForSelection([]);
        fetchAllDependencies();
        setSelectedSpecificationToLoad('');
    } catch (err) {
        if (axios.isAxiosError(err) && err.response && err.response.data) {
            let errorMessages = '';
            for (const key in err.response.data) {
                if (Object.prototype.hasOwnProperty.call(err.response.data, key)) {
                    const errorValue = err.response.data[key];
                    if (Array.isArray(errorValue)) {
                        errorMessages += `${key}: ${errorValue.join(', ')}\n`;
                    } else {
                        errorMessages += `${key}: ${errorValue}\n`;
                    }
                }
            }
            showMessage('שגיאה', `שגיאה בטעינת המפרט: \n${errorMessages || 'שגיאה לא ידועה.'}`, 'danger');
        } else {
            showMessage('שגיאה', 'אירעה שגיאה בעת טעינת המפרט. אנא נסה שוב.', 'danger');
        }
    }
  };
  
  const debouncedUpdateQuantity = useCallback(
    _.debounce(async (itemId: number, newQuantity: number, type: 'equipment' | 'consumable') => {
        const url = type === 'equipment'
            ? buildApiUrl(`events/event-equipment/${itemId}/`)
            : buildApiUrl(`events/event-consumables/${itemId}/`);
        const payload = type === 'equipment' ? { quantity_needed: newQuantity } : { quantity_used: newQuantity };
        try {
            await axios.patch(url, payload);
        } catch (err) {
            showMessage('שגיאה', 'לא ניתן היה לעדכן את הכמות.', 'danger');
            fetchAllDependencies();
        }
    }, 1000),
    [fetchAllDependencies]
  );

  const handleEquipmentNoteDraftChange = (itemId: number, value: string) => {
    setEquipmentNotesDraft(prev => ({ ...prev, [itemId]: value }));
  };

  const handleEquipmentNoteBlur = async (itemId: number) => {
    const targetItem = eventEquipment.find(item => Number(item.id) === itemId);
    if (!targetItem) return;

    const nextNote = (equipmentNotesDraft[itemId] ?? '').trim();
    const currentNote = (targetItem.notes || '').trim();
    if (nextNote === currentNote) return;

    try {
      const payload = { notes: nextNote || null };
      const response = await axios.patch(buildApiUrl(`events/event-equipment/${itemId}/`), payload);
      const updatedItem: EventEquipmentItem = response.data;

      setEventEquipment(prev => prev.map(item => (Number(item.id) === itemId ? updatedItem : item)));
      setEquipmentNotesDraft(prev => ({ ...prev, [itemId]: updatedItem.notes || '' }));
    } catch (err) {
      showMessage('שגיאה', 'לא ניתן היה לעדכן את ההערה לפריט.', 'danger');
      setEquipmentNotesDraft(prev => ({ ...prev, [itemId]: targetItem.notes || '' }));
    }
  };



  const handleQuantityKeyDown = (e: React.KeyboardEvent, itemId: number | undefined, currentQuantity: number, type: 'equipment' | 'consumable') => {
    if (itemId === undefined) return;
    
    // רק לחצים למעלה/למטה
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault(); // מונע את ההתנהגות הרגילה של השדה
    
    const inventoryItem = allInventoryEquipment.find(invEq => {
      if (type === 'equipment') {
        const eventItem = eventEquipment.find(eq => eq.id === itemId);
        return invEq.id === eventItem?.equipment;
      }
      return false;
    });
    
    // קביעת גודל הקייס - ברירת מחדל 1
    const caseSize = (inventoryItem && inventoryItem.case_quantity > 1) ? inventoryItem.case_quantity : 1;
    let newQuantity = currentQuantity;
    
    // בדיקת מקסימום - רק אם יש הגבלה של מלאי זמין
    const eventItem = eventEquipment.find(eq => eq.id === itemId);
    const availableOutsideEvent = (eventItem?.equipment && availableEquipmentMap.get(eventItem.equipment)) || 0;
    const maxAllowed = availableOutsideEvent;
    
    if (e.key === 'ArrowUp') {
      newQuantity += caseSize;
      if (newQuantity > maxAllowed && maxAllowed > 0) {
        newQuantity = maxAllowed;
      }
    } else if (e.key === 'ArrowDown') {
      // אם אנחנו במקסימום שלא מתאים לקייס, אפס לקייס הקרוב
      if (currentQuantity === maxAllowed && maxAllowed > 0 && maxAllowed % caseSize !== 0) {
        newQuantity = Math.floor(maxAllowed / caseSize) * caseSize;
      } else {
        newQuantity = Math.max(0, newQuantity - caseSize);
      }
    }
    
    handleQuantityChange(itemId, newQuantity.toString(), type);
    }
  };

  const handleQuantityChange = (itemId: number | undefined, value: string, type: 'equipment' | 'consumable') => {
    if (itemId === undefined) return;
    
    let requestedQuantity = Number(value);
    if (isNaN(requestedQuantity) || requestedQuantity < 0) requestedQuantity = 0;
    
    // זיהוי חצים רק אם השינוי הוא 1 בדיוק והערכים סבירים
    if (type === 'equipment') {
      const eventItem = eventEquipment.find(eq => eq.id === itemId);
      const currentQuantity = eventItem?.quantity_needed || 0;
      const difference = Math.abs(requestedQuantity - currentQuantity);
      
      // זיהוי חץ: השינוי הוא 1 בדיוק, והערכים סבירים (לא הקלדה ידנית)
      if (difference === 1 && requestedQuantity !== 0 && currentQuantity >= 0 && currentQuantity <= 1000) {
        const inventoryItem = allInventoryEquipment.find(invEq => invEq.id === eventItem?.equipment);
        if (inventoryItem && inventoryItem.case_quantity > 1) {
          const caseSize = inventoryItem.case_quantity;
          let newQuantity = currentQuantity;
          
        if (requestedQuantity > currentQuantity) {
          // חץ למעלה - הוסף קייס שלם
          newQuantity += caseSize;
          // בדיקת מקסימום
          const availableOutsideEvent = (eventItem?.equipment && availableEquipmentMap.get(eventItem.equipment)) || 0;
          const maxAllowed = availableOutsideEvent;
          if (newQuantity > maxAllowed && maxAllowed > 0) {
            newQuantity = maxAllowed;
          }
        } else {
          // חץ למטה - הפחת קייס שלם
          // אם אנחנו במקסימום שלא מתאים לקייס, אפס לקייס הקרוב
          const availableOutsideEvent = (eventItem?.equipment && availableEquipmentMap.get(eventItem.equipment)) || 0;
          const maxAllowed = availableOutsideEvent;
          if (currentQuantity === maxAllowed && maxAllowed > 0 && maxAllowed % caseSize !== 0) {
            newQuantity = Math.floor(maxAllowed / caseSize) * caseSize;
          } else {
            newQuantity = Math.max(0, currentQuantity - caseSize);
          }
        }
          
          
          // עדכון מיידי של ה-state
          const updatedEquipment = eventEquipment.map(item => 
            item.id === itemId ? { ...item, quantity_needed: newQuantity } : item
          );
          setEventEquipment(updatedEquipment);
          debouncedUpdateQuantity(itemId, newQuantity, type);
          return;
        }
      }
    }

    if (type === 'equipment') {
        const eventItem = eventEquipment.find(eq => eq.id === itemId);
        const inventoryItem = allInventoryEquipment.find(invEq => invEq.id === eventItem?.equipment);

        // מקסימום מותר = המלאי הזמין הכולל לאירוע
        const availableOutsideEvent = (eventItem?.equipment && availableEquipmentMap.get(eventItem.equipment)) || 0;
        const currentEventQty = eventItem?.quantity_needed || 0;
        const maxAllowed = availableOutsideEvent; // רק מה שזמין במלאי
        

    // הצמדה למדרגות קייס - תמיד עגל לקייס הקרוב
    let newQuantity = requestedQuantity;
    
    // אם יש כמות לקייס, עגל לקייס הקרוב (רק אם זה לא חץ)
    if (inventoryItem && inventoryItem.case_quantity > 1 && newQuantity > 0) {
        const caseSize = inventoryItem.case_quantity;
        
        // בדיקה אם זה חץ - אם השינוי הוא 1 בדיוק, זה חץ
        const currentQuantity = eventItem?.quantity_needed || 0;
        const difference = Math.abs(requestedQuantity - currentQuantity);
        
        // אם זה לא חץ (שינוי גדול מ-1), עגל לקייס הקרוב עם דיליי
        if (difference > 1) {
            // ביטול timeout קודם אם קיים
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            
        // דיליי ארוך יותר כדי לאפשר למשתמש לסיים להקליד לפני הצמדה לקייס
        typingTimeoutRef.current = setTimeout(() => {
            let adjustedQuantity = requestedQuantity;
            
            // בדיקת מקסימום קודם
            const availableOutsideEvent = (eventItem?.equipment && availableEquipmentMap.get(eventItem.equipment)) || 0;
            const maxAllowed = availableOutsideEvent;
            
            // אם הכמות גדולה מהמקסימום, תקן למקסימום
            if (adjustedQuantity > maxAllowed) {
                adjustedQuantity = maxAllowed;
            }
            
            // עגל לקייס הקרוב רק אם לא הגענו למקסימום
            if (adjustedQuantity < maxAllowed) {
                adjustedQuantity = Math.round(adjustedQuantity / caseSize) * caseSize;
                
                // אם התוצאה היא 0 אבל המשתמש רצה כמות חיובית, תן לו קייס אחד
                if (adjustedQuantity === 0 && requestedQuantity > 0) {
                    adjustedQuantity = caseSize;
                }
            }
            
            
            // עדכון הכמות
            const updatedEquipment = eventEquipment.map(item => 
                item.id === itemId ? { ...item, quantity_needed: adjustedQuantity } : item
            );
            setEventEquipment(updatedEquipment);
            debouncedUpdateQuantity(itemId, adjustedQuantity, type);
        }, 2000); // דיליי של 2000ms (2 שניות)
        }
    }

    // הגבלת טווח - לא יותר מהמלאי הזמין
    if (newQuantity > maxAllowed) {
        // תיקון אוטומטי למקסימום זמין - ללא הצמדה לקייס
        newQuantity = maxAllowed;
        showMessage('תיקון אוטומטי', `הכמות תוקנה אוטומטית ל-${newQuantity} (המקסימום הזמין).`, 'info');
    }

        // אם הכמות 0, נאפשר את זה (למחיקה)
        if (newQuantity === 0) {
            const updatedEquipment = eventEquipment.map(item => 
                item.id === itemId ? { ...item, quantity_needed: 0 } : item
            );
            setEventEquipment(updatedEquipment);
            debouncedUpdateQuantity(itemId, 0, type);
            return;
        }


        // עדכון מיידי של ה-state
        const updatedEquipment = eventEquipment.map(item => 
            item.id === itemId ? { ...item, quantity_needed: newQuantity } : item
        );
        setEventEquipment(updatedEquipment);
      
        // עדכון בשרת (עם debounce)
        debouncedUpdateQuantity(itemId, newQuantity, type);

    } else {
        const updatedConsumables = eventConsumables.map(item => 
            item.id === itemId ? { ...item, quantity_used: requestedQuantity } : item
        );
        setEventConsumables(updatedConsumables);

        debouncedUpdateQuantity(itemId, requestedQuantity, type);
    }
  };
  
  const handleAddQuantityChange = (itemId: number | undefined, value: string) => {
    if (itemId === undefined) return;
    let requestedQuantity = Number(value);
    if (isNaN(requestedQuantity) || requestedQuantity < 0) requestedQuantity = 0;

    const item = allInventoryEquipment.find(eq => eq.id === itemId);
    const availableQuantity = availableEquipmentMap.get(itemId) || 0;
    
    const existingTimeout = addQuantityTypingTimeoutsRef.current.get(itemId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      addQuantityTypingTimeoutsRef.current.delete(itemId);
    }

    // הגבלה לפי המלאי הזמין
    let newQuantity = requestedQuantity;

    if (newQuantity > availableQuantity) {
      const originalQuantity = newQuantity;
      newQuantity = availableQuantity;
      
      // הצגת הודעה על התיקון האוטומטי
      const itemName = item?.name || 'פריט לא ידוע';
      showMessage('תיקון אוטומטי', `הכמות עבור "${itemName}" תוקנה אוטומטית מ-${originalQuantity} ל-${newQuantity} (המקסימום הזמין).`, 'info');
    }

    // הצמדה לקייס עם דיליי בזמן הקלדה (כדי לא להפריע למשתמש)
    if (item && item.case_quantity > 1 && newQuantity > 0 && newQuantity < availableQuantity) {
      const caseSize = item.case_quantity;
      setQuantitiesToAdd(prev => ({ ...prev, [itemId]: String(newQuantity) }));

      const timeout = setTimeout(() => {
        const adjustedQuantity = Math.round(newQuantity / caseSize) * caseSize;
        setQuantitiesToAdd(prev => ({ ...prev, [itemId]: String(adjustedQuantity) }));
        addQuantityTypingTimeoutsRef.current.delete(itemId);
      }, 2000);

      addQuantityTypingTimeoutsRef.current.set(itemId, timeout);
      return;
    }

    setQuantitiesToAdd(prev => ({ ...prev, [itemId]: String(newQuantity) }));
  };

  useEffect(() => {
    return () => {
      addQuantityTypingTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      addQuantityTypingTimeoutsRef.current.clear();
    };
  }, []);
  
  const handleDecrementAddQuantity = (itemId: number | undefined) => {
    if (itemId === undefined) return;
    const item = allInventoryEquipment.find(eq => eq.id === itemId);
    const currentQty = Number(quantitiesToAdd[itemId] || 0);

    if (item && item.case_quantity > 1) {
        const newQty = Math.max(0, currentQty - item.case_quantity);
        setQuantitiesToAdd(prev => ({...prev, [itemId]: String(newQty)}));
    } else {
        const newQty = Math.max(0, currentQty - 1);
        setQuantitiesToAdd(prev => ({...prev, [itemId]: String(newQty)}));
    }
  };
  
  const handleIncrementAddQuantity = (itemId: number | undefined) => {
      if (itemId === undefined) return;
      const item = allInventoryEquipment.find(eq => eq.id === itemId);
      const currentQty = Number(quantitiesToAdd[itemId] || 0);
      const availableQuantity = availableEquipmentMap.get(itemId) || 0;
      
      let newQty: number;
      if (item && item.case_quantity > 1) {
          newQty = currentQty + item.case_quantity;
      } else {
          newQty = currentQty + 1;
      }
      
      // הגבלה לפי המלאי הזמין
      if (newQty > availableQuantity) {
          const originalQty = newQty;
          newQty = availableQuantity;
          
          // הצגת הודעה על התיקון האוטומטי
          const itemName = item?.name || 'פריט לא ידוע';
          showMessage('תיקון אוטומטי', `הכמות עבור "${itemName}" תוקנה אוטומטית מ-${originalQty} ל-${newQty} (המקסימום הזמין).`, 'info');
      }
      
      setQuantitiesToAdd(prev => ({...prev, [itemId]: String(newQty)}));
  };

  const handleRentedDateChange = async (itemId: number, type: 'arrival' | 'departure', value: string) => {
    const item = eventEquipment.find(eq => eq.id === itemId);
    if (!item) return;

    const newItemData = {
      ...item,
      [type === 'arrival' ? 'rented_arrival_date' : 'rented_departure_date']: value || null,
    };

    try {
      await axios.patch(buildApiUrl(`events/event-equipment/${itemId}/`), newItemData);
      showMessage('הצלחה', `תאריכי הגעה/עזיבה לפריט עודכנו בהצלחה!`, 'success');
      fetchAllDependencies();
    } catch (err) {
      console.error("שגיאה בעדכון תאריכי הציוד המושכר:", err);
      showMessage('שגיאה', 'אירעה שגיאה בעת עדכון תאריכי הציוד המושכר.', 'danger');
    }
  };

  const fetchEventsToCopyFrom = async () => {
      try {
          const params = { search: copyEventSearchQuery };
          const response = await axios.get(buildApiUrl('events/events/'), { params });
          setEventsToCopyFrom(response.data.filter((e: Event) => e.id !== eventIdNumber));
      } catch (err) {
          console.error("שגיאה בטעינת אירועים להעתקה:", err);
          showMessage('שגיאה', 'לא ניתן לטעון אירועים להעתקה.', 'danger');
      }
  };

  useEffect(() => {
      if (showCopyFromEventModal) {
          const handler = setTimeout(() => fetchEventsToCopyFrom(), 300);
          return () => clearTimeout(handler);
      }
  }, [showCopyFromEventModal, copyEventSearchQuery]);

  const handleSelectEventToCopyFrom = async (selectedId: number | '') => {
      setSelectedEventToCopyFrom(selectedId);
      if (selectedId) {
          try {
              const response = await axios.get(buildApiUrl(`events/events/${selectedId}/`));
              const sourceEvent = response.data;
              const items: RecommendedItem[] = [];

              if (sourceEvent.eventequipment_set) {
                  sourceEvent.eventequipment_set.forEach((item: EventEquipmentItem) => {
                      if (item.equipment) {
                          const itemName = allInventoryEquipment.find(eq => eq.id === item.equipment)?.name || item.equipment_name || 'פריט לא ידוע';
                          items.push({ dedupeKey: `eq-${item.equipment}`, id: item.equipment, name: itemName, quantity: item.quantity_needed, reason: 'ציוד', reasons: undefined, isSelected: true, type: 'equipment' });
                      } else {
                          const rentedName = item.rented_item_name || 'פריט מושכר';
                          items.push({ dedupeKey: `copy-rent-${item.id}`, id: item.id as number, name: rentedName, quantity: item.quantity_needed, reason: 'מושכר', reasons: undefined, isSelected: true, type: 'equipment'});
                      }
                  });
              }

              if (sourceEvent.consumables) {
                  sourceEvent.consumables.forEach((item: EventConsumableItem) => {
                      if (item.consumable) {
                          const consumableName = allConsumables.find(c => c.id === item.consumable)?.name || item.consumable_name || 'מתכלה לא ידוע';
                          items.push({ dedupeKey: `co-${item.consumable}`, id: item.consumable, name: consumableName, quantity: item.quantity_used, reason: 'מתכלה', reasons: undefined, isSelected: true, type: 'consumable' });
                      }
                  });
              }
              setCopiedItemsForSelection(items);
          } catch (err) {
              console.error("שגיאה בטעינת פריטי אירוע להעתקה:", err);
              showMessage('שגיאה', 'לא ניתן לטעון פריטים מהאירוע הנבחר.', 'danger');
              setCopiedItemsForSelection([]);
          }
      } else {
          setCopiedItemsForSelection([]);
      }
  };

  const handleCopyItemsSelection = async (action: 'accept_all' | 'reject_all' | 'accept_selected') => {
    if (action === 'reject_all') {
      setCopiedItemsForSelection([]);
      setShowCopyFromEventModal(false);
      return;
    }

    let itemsToProcess = [];
    if (action === 'accept_all') {
      itemsToProcess = copiedItemsForSelection;
    } else {
      itemsToProcess = copiedItemsForSelection.filter(item => item.isSelected);
    }
    
    if (itemsToProcess.length === 0) {
        showMessage('מידע', 'לא נבחרו פריטים להעתקה.', 'info');
        return;
    }

    const equipmentToUpdate: { id: number; quantity_needed: number }[] = [];
    const equipmentToCreate: { event: number; equipment: number | null; quantity_needed: number; is_rented_item: boolean; rented_item_name?: string; rented_item_category?: string; rented_item_subcategory?: string; rental_supplier_name?: string; rented_arrival_date?: string; rented_departure_date?: string }[] = [];
    const consumablesToUpdate: { id: number; quantity_used: number }[] = [];
    const consumablesToCreate: { event: number; consumable: number; quantity_used: number }[] = [];

    for (const item of itemsToProcess) {
        if (item.type === 'equipment') {
            const isRented = item.reason === 'מושכר';
            const existingItem = eventEquipment.find(eq => (isRented ? eq.rented_item_name === item.name : eq.equipment === item.id) && eq.is_rented_item === isRented);

            if (!isRented) {
                const availableQty = availableEquipmentMap.get(item.id) || 0;
                const currentEventQty = existingItem ? existingItem.quantity_needed : 0;
                const newTotalQty = currentEventQty + item.quantity;
                if (newTotalQty > availableQty) {
                    const itemName = allInventoryEquipment.find(eq => eq.id === item.id)?.name || 'פריט לא ידוע';
                    showMessage('שגיאת מלאי', `לא ניתן להעתיק ${item.quantity} יחידות של ${itemName}. יש רק ${availableQty} זמינות לאירוע.`, 'danger');
                    return;
                }
            }

            if (existingItem) {
                const newTotalQty = existingItem.quantity_needed + item.quantity;
                equipmentToUpdate.push({ id: existingItem.id as number, quantity_needed: newTotalQty });
            } else {
                if (isRented) {
                    // מציאת הפריט המקורי מהאירוע המקור כדי לקבל את כל הפרטים
                    const sourceEvent = eventsToCopyFrom.find(e => e.id === selectedEventToCopyFrom);
                    const sourceItem = (sourceEvent as any)?.eventequipment_set?.find((eq: any) => eq.rented_item_name === item.name);
                    
                    equipmentToCreate.push({ 
                        event: eventIdNumber, 
                        equipment: null, 
                        quantity_needed: item.quantity, 
                        is_rented_item: true, 
                        rented_item_name: item.name,
                        rented_item_category: sourceItem?.rented_item_category || 'כללי',
                        rented_item_subcategory: sourceItem?.rented_item_subcategory || 'כללי',
                        rental_supplier_name: sourceItem?.rental_supplier_name || '',
                        rented_arrival_date: sourceItem?.rented_arrival_date || null,
                        rented_departure_date: sourceItem?.rented_departure_date || null
                    });
                } else {
                    equipmentToCreate.push({ event: eventIdNumber, equipment: item.id, quantity_needed: item.quantity, is_rented_item: false });
                }
            }

        } else if (item.type === 'consumable') {
            const existingItem = eventConsumables.find(c => c.consumable === item.id);
            const currentEventQty = existingItem ? existingItem.quantity_used : 0;
            const newTotalQty = currentEventQty + item.quantity;

            if (existingItem) {
                consumablesToUpdate.push({ id: existingItem.id, quantity_used: newTotalQty });
            } else {
                consumablesToCreate.push({ event: eventIdNumber, consumable: item.id, quantity_used: newTotalQty });
            }
        }
    }

    try {
        await Promise.all([
            ...equipmentToUpdate.map(item => axios.patch(buildApiUrl(`events/event-equipment/${item.id}/`), { quantity_needed: item.quantity_needed })),
            ...equipmentToCreate.map(item => axios.post(buildApiUrl('events/event-equipment/'), item)),
            ...consumablesToUpdate.map(item => axios.patch(buildApiUrl(`events/event-consumables/${item.id}/`), { quantity_used: item.quantity_used })),
            ...consumablesToCreate.map(item => axios.post(buildApiUrl('events/event-consumables/'), item)),
        ]);
        showMessage('הצלחה', 'הפריטים הועתקו בהצלחה לאירוע הנוכחי!', 'success');
        setShowCopyFromEventModal(false);
        fetchAllDependencies();
        setCopiedItemsForSelection([]);
    } catch (err) {
        console.error("שגיאה בהעתקת פריטים:", err);
        showMessage('שגיאה', 'אירעה שגיאה בעת העתקת הפריטים.', 'danger');
    }
  };

  // פונקציה לסינון הציוד מהמלאי לפי חיפוש
  const filteredInventoryEquipment = useMemo(() => {
      let filtered = [...allInventoryEquipment];
      
      // סינון לפי טקסט חיפוש
      if (searchTerm) {
          filtered = filtered.filter(item => 
              item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (item.category_name && item.category_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
              (item.subcategory_name && item.subcategory_name.toLowerCase().includes(searchTerm.toLowerCase()))
          );
      }
      
      // סינון לפי קטגוריה
      if (selectedCategory) {
          filtered = filtered.filter(item => item.category_name === selectedCategory);
      }
      
      // סינון לפי תת-קטגוריה
      if (selectedSubcategory) {
          filtered = filtered.filter(item => item.subcategory_name === selectedSubcategory);
      }
      
      return filtered;
  }, [allInventoryEquipment, searchTerm, selectedCategory, selectedSubcategory]);

  // קבוצת הציוד המסונן מהמלאי
  const filteredGroupedInventory = useMemo(() => groupInventoryEquipment(filteredInventoryEquipment, sortedCategories), [filteredInventoryEquipment, sortedCategories]);
  
  // פונקציה לסינון הפריטים שנבחרו לאירוע
  const filteredSelectedItems = useMemo(() => {
      let filtered = [...eventEquipment, ...eventConsumables];
      
      // סינון לפי טקסט חיפוש
      if (selectedItemsSearchTerm) {
          filtered = filtered.filter(item => {
              if ('equipment_name' in item) {
                  return (item.equipment_name || item.rented_item_name || '').toLowerCase().includes(selectedItemsSearchTerm.toLowerCase());
              } else if ('consumable_name' in item) {
                  return (item.consumable_name || '').toLowerCase().includes(selectedItemsSearchTerm.toLowerCase());
              }
              return false;
          });
      }
      
      // סינון לפי קטגוריה
      if (selectedItemsSelectedCategory) {
          filtered = filtered.filter(item => {
              if ('equipment_name' in item) {
                  return (item.category_name || item.rented_item_category || '') === selectedItemsSelectedCategory;
              } else if ('consumable_name' in item) {
                  return selectedItemsSelectedCategory === 'מתכלים';
              }
              return false;
          });
      }
      
      // סינון לפי תת-קטגוריה
      if (selectedItemsSelectedSubcategory) {
          filtered = filtered.filter(item => {
              if ('equipment_name' in item) {
                  return (item.subcategory_name || item.rented_item_subcategory || '') === selectedItemsSelectedSubcategory;
              }
              return false;
          });
      }
      
      return filtered;
  }, [eventEquipment, eventConsumables, selectedItemsSearchTerm, selectedItemsSelectedCategory, selectedItemsSelectedSubcategory]);
  
  // קבוצת הפריטים המסוננים שנבחרו
  const filteredGroupedSelectedItems = useMemo(() => groupEventItems(filteredSelectedItems, sortedCategories), [filteredSelectedItems, sortedCategories]);

  // קבלת תת-קטגוריות לפי קטגוריה נבחרת
  const availableSubcategories = useMemo(() => {
      if (!selectedCategory) return [];
      
      const subcategories = new Set<string>();
      allInventoryEquipment.forEach(item => {
          if (item.category_name === selectedCategory) {
              subcategories.add(item.subcategory_name || 'כללי');
          }
      });
      
      return Array.from(subcategories).sort();
  }, [allInventoryEquipment, selectedCategory]);
  
  // קבלת תת-קטגוריות עבור הפריטים שנבחרו
  const availableSelectedItemsSubcategories = useMemo(() => {
      if (!selectedItemsSelectedCategory) return [];
      
      const subcategories = new Set<string>();
      [...eventEquipment, ...eventConsumables].forEach(item => {
          if ('equipment_name' in item) {
              if (item.category_name === selectedItemsSelectedCategory || item.rented_item_category === selectedItemsSelectedCategory) {
                  subcategories.add(item.subcategory_name || item.rented_item_subcategory || 'כללי');
              }
          }
      });
      
      return Array.from(subcategories).sort();
  }, [eventEquipment, eventConsumables, selectedItemsSelectedCategory]);

  // פונקציה לסינון מתכלים לפי חיפוש
  const filteredConsumables = useMemo(() => {
      if (!searchTerm) return allConsumables;
      
      return allConsumables.filter(item => 
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.unit && item.unit.toLowerCase().includes(searchTerm.toLowerCase()))
      );
  }, [allConsumables, searchTerm]);

  // ניקוי סינון
  const clearFilters = () => {
      setSearchTerm('');
      setSelectedCategory('');
      setSelectedSubcategory('');
  };
  
  // ניקוי סינון פריטים שנבחרו
  const clearSelectedItemsFilters = () => {
      setSelectedItemsSearchTerm('');
      setSelectedItemsSelectedCategory('');
      setSelectedItemsSelectedSubcategory('');
  };

  // פונקציה לטיפול בלחיצה על פריט ציוד
  const handleEquipmentRowClick = (equipmentItem: EventEquipmentItem) => {
    if (equipmentItem.is_rented_item) {
      // עבור ציוד מושכר, נציג הודעה שאין פרטים מלאים
      showMessage('מידע', 'לציוד מושכר אין פרטים מלאים במערכת. פרטי הציוד המושכר מוצגים בטבלה.', 'info');
      return;
    }
    
    // מצא את הציוד המלא במלאי
    const fullEquipment = allInventoryEquipment.find(eq => eq.id === equipmentItem.equipment);
    if (fullEquipment) {
      setSelectedEquipmentForDetails(fullEquipment);
      setShowEquipmentDetailModal(true);
    } else {
      showMessage('שגיאה', 'לא ניתן למצוא פרטי ציוד מלאים.', 'danger');
    }
  };

  // פיצול התיאור לחמישה חלקים: הגברה | תאורה | וידאו | חשמל | כללי
  const specParts = useMemo(() => {
    if (!event || !event.description) return [];
    
    const [descAmplification = '', descLighting = '', descVideo = '', descElectrical = '', descGeneral = ''] =
      event.description.split('---SPEC_DELIMITER---');
    
    return [
      { label: 'הגברה', value: descAmplification },
      { label: 'תאורה', value: descLighting },
      { label: 'וידאו', value: descVideo },
      { label: 'חשמל', value: descElectrical },
      { label: 'כללי', value: descGeneral },
    ].filter(p => p.value && p.value.trim() !== '');
  }, [event]);

  if (loading && selectedEventId) return <Spinner animation="border" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <Container fluid>
      <div className="app-sticky-page-toolbar mb-4 d-print-none">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="mb-0">ניהול ציוד</h2>
          <Button variant="success" onClick={openAddNewEquipmentModal} className="me-2">
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            הוסף ציוד חדש
          </Button>
        </div>
        <div className="d-flex justify-content-between">
          <Button variant="secondary" onClick={onBack} className="me-2">
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
            חזור לרשימת האירועים
          </Button>
          <Button variant="secondary" onClick={() => onBackToDetails(selectedEventId as number)} className="me-2">
            <FontAwesomeIcon icon={faEye} className="me-1" />
            חזרה לפרטי האירוע
          </Button>
          <Button variant="secondary" onClick={onBackToCalendar}>
            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
            חזרה ללוח שנה
          </Button>
        </div>
      </div>

      {/* Event Selection */}
      <Card className="mb-4 d-print-none">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h4>בחר אירוע לניהול ציוד</h4>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={() => setShowEventFilters(!showEventFilters)}
            >
              <FontAwesomeIcon icon={faFilter} className="me-1" />
              סינון וחיפוש
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {loadingEvents ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              טוען אירועים...
            </div>
          ) : errorEvents ? (
            <Alert variant="danger">{errorEvents}</Alert>
          ) : (
            <>
              {/* Search and Filter Controls */}
              {showEventFilters && (
                <div className="mb-4 p-3 border rounded bg-light">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>חיפוש</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                          <Form.Control
                            placeholder="חפש לפי שם אירוע, לקוח או מיקום..."
                            value={eventSearchTerm}
                            onChange={(e) => setEventSearchTerm(e.target.value)}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>סטטוס</Form.Label>
                        <Form.Select 
                          value={selectedStatusFilter} 
                          onChange={(e) => setSelectedStatusFilter(e.target.value)}
                        >
                          <option value="">כל הסטטוסים</option>
                          {Object.entries(STATUS_LABELS).map(([status, label]) => (
                            <option key={status} value={status}>{label}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>תאריך התחלה</Form.Label>
                        <Form.Control
                          type="date"
                          value={startDateFilter}
                          onChange={(e) => setStartDateFilter(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>תאריך סיום</Form.Label>
                        <Form.Control
                          type="date"
                          value={endDateFilter}
                          onChange={(e) => setEndDateFilter(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={9}>
                      <div className="d-flex align-items-end h-100">
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          onClick={clearEventFilters}
                          className="me-2"
                        >
                          <FontAwesomeIcon icon={faTimes} className="me-1" />
                          נקה סינונים
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          onClick={() => setShowEventFilters(false)}
                        >
                          <FontAwesomeIcon icon={faTimes} className="me-1" />
                          סגור סינונים
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  
                  {/* Search Results Info */}
                  {(eventSearchTerm || selectedStatusFilter || startDateFilter || endDateFilter) && (
                    <Alert variant="info" className="mb-0">
                      <FontAwesomeIcon icon={faSearch} className="me-2" />
                      נמצאו {filteredEvents.length} אירועים מתוך {events.length}
                      {eventSearchTerm && <span className="ms-2">(חיפוש: "{eventSearchTerm}")</span>}
                      {selectedStatusFilter && <span className="ms-2">(סטטוס: {STATUS_LABELS[selectedStatusFilter]})</span>}
                      {startDateFilter && <span className="ms-2">(מ-{new Date(startDateFilter).toLocaleDateString('he-IL')})</span>}
                      {endDateFilter && <span className="ms-2">(עד {new Date(endDateFilter).toLocaleDateString('he-IL')})</span>}
                    </Alert>
                  )}
                </div>
              )}

              <Form.Group>
                <Form.Label>בחר אירוע</Form.Label>
                <Form.Select 
                  value={selectedEventId} 
                  onChange={(e) => setSelectedEventId(parseInt(e.target.value) || '')}
                  size="lg"
                >
                  <option value="">בחר אירוע...</option>
                  {filteredEvents.length > 0 ? (
                    filteredEvents.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.event_name} ({event.client_name || event.free_text_client}) - {STATUS_LABELS[event.status] || event.status} - {new Date(event.start_date).toLocaleDateString('he-IL')}
                      </option>
                    ))
                  ) : (
                    <option disabled>לא נמצאו אירועים התואמים לחיפוש</option>
                  )}
                </Form.Select>
                <Form.Text className="text-muted">
                  ניתן לבחור אירועים בכל סטטוס: מתוכנן, בהכנה, פעיל, הסתיים, בוטל וללא רשימת ציוד
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Card.Body>
      </Card>

      {selectedEventId && event ? (
        <>
        <Card className="mb-4 d-print-none">
          <Card.Header>
            <h2>ניהול ציוד עבור: {event.event_name}</h2>
          <p className="mb-0"><strong>לקוח:</strong> {event.client_name || event.free_text_client} | <strong>תאריכים:</strong> {new Date(event.start_date).toLocaleDateString('he-IL')} - {new Date(event.end_date).toLocaleDateString('he-IL')}</p>
          {specParts.length > 0 && (
            <div className="mt-2">
              <strong>מפרטי אירוע:</strong>
              <ul className="mb-0">
                {specParts.map((p, idx) => (
                  <li key={idx}><strong>{p.label}:</strong> {p.value}</li>
                ))}
              </ul>
            </div>
          )}
        </Card.Header>
      </Card>

      {/* התראה על ציוד לא זמין */}
      {equipmentAvailability?.equipment_availability?.has_unavailable && (
        <Alert variant="warning" className="mb-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <strong>אזהרה:</strong> חלק מהציוד לא זמין לתאריכי האירוע!
          <br />
          <small>
            {equipmentAvailability.equipment_availability.unavailable_count} פריטים לא זמינים מתוך {equipmentAvailability.equipment_availability.total_equipment} פריטי ציוד.
          </small>
          
          <div className="mt-2">
            <h6>פירוט הציוד הלא זמין:</h6>
            <div className="list-group">
              {equipmentAvailability.equipment_availability.unavailable_equipment.map((item: any, index: number) => (
                <div key={index} className="list-group-item list-group-item-danger">
                  <div className="d-flex w-100 justify-content-between">
                    <h6 className="mb-1">{item.equipment_name}</h6>
                    <small>
                      {item.quantity_needed} יחידות נדרשות
                      {(() => {
                        const inv = allInventoryEquipment.find(invEq => invEq.id === item.equipment);
                        const label = formatCasesLabel(Number(item.quantity_needed || 0), inv?.case_quantity);
                        return label ? <span className="text-muted ms-2">({label})</span> : null;
                      })()}
                      {item.unavailable_quantity && (
                        <span className="text-danger ms-2">
                          ({item.unavailable_quantity} לא זמינות)
                        </span>
                      )}
                    </small>
                  </div>
                  <p className="mb-1 text-muted">
                    <strong>סיבה:</strong> {item.availability_reason}
                    {item.available_quantity !== undefined && (
                      <span className="ms-2">
                        <strong>זמין במלאי:</strong> {item.available_quantity} יחידות
                      </span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Alert>
      )}

      <Row>
        <Col lg={7}>
          <Card>
            <Card.Header>רשימת פריטים לאירוע</Card.Header>
            <Card.Body>
              
              {/* חיפוש ברשימת הפריטים שנבחרו */}
              {groupedEventItems && Object.keys(groupedEventItems).length > 0 && (
                  <div className="mb-3 equipment-manager-selected-items-search-container">
                      <InputGroup className="mb-2 equipment-manager-selected-items-search">
                          <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                          <Form.Control
                              className="equipment-manager-selected-items-search-input"
                              placeholder="חפש פריטים שנבחרו..."
                              value={selectedItemsSearchTerm}
                              onChange={(e) => setSelectedItemsSearchTerm(e.target.value)}
                          />
                          <Button 
                              className="equipment-manager-selected-items-filter-button"
                              variant="outline-secondary" 
                              onClick={() => setShowSelectedItemsSearchFilters(!showSelectedItemsSearchFilters)}
                          >
                              <FontAwesomeIcon icon={faFilter} />
                          </Button>
                      </InputGroup>

                      {showSelectedItemsSearchFilters && (
                          <div className="mb-3 equipment-manager-selected-items-filters-container">
                              <Row>
                                  <Col md={6}>
                                      <Form.Label>קטגוריה</Form.Label>
                                      <Form.Select 
                                          className="equipment-manager-selected-items-category-select"
                                          value={selectedItemsSelectedCategory} 
                                          onChange={(e) => setSelectedItemsSelectedCategory(e.target.value)}
                                      >
                                          <option value="">כל הקטגוריות</option>
                                          {sortedCategories.map(cat => (
                                              <option key={cat} value={cat}>{cat}</option>
                                          ))}
                                          <option value="מתכלים">מתכלים</option>
                                      </Form.Select>
                                  </Col>
                                  <Col md={6}>
                                      <Form.Label>תת-קטגוריה</Form.Label>
                                      <Form.Select 
                                          className="equipment-manager-selected-items-subcategory-select"
                                          value={selectedItemsSelectedSubcategory} 
                                          onChange={(e) => setSelectedItemsSelectedSubcategory(e.target.value)}
                                          disabled={!selectedItemsSelectedCategory}
                                      >
                                          <option value="">כל התת-קטגוריות</option>
                                          {availableSelectedItemsSubcategories.map(sub => (
                                              <option key={sub} value={sub}>{sub}</option>
                                          ))}
                                      </Form.Select>
                                  </Col>
                              </Row>
                              <div className="d-flex gap-2 mt-2">
                                  <Button 
                                      className="equipment-manager-selected-items-clear-filters-button"
                                      variant="secondary" 
                                      size="sm" 
                                      onClick={clearSelectedItemsFilters}
                                  >
                                      <FontAwesomeIcon icon={faTimes} /> נקה סינונים
                                  </Button>
                                  <Button variant="outline-secondary" size="sm" onClick={() => setShowSelectedItemsSearchFilters(false)}>
                                      <FontAwesomeIcon icon={faTimes} /> סגור סינונים
                                  </Button>
                              </div>
                          </div>
                      )}

                      {/* הודעת תוצאות החיפוש */}
                      {(selectedItemsSearchTerm || selectedItemsSelectedCategory || selectedItemsSelectedSubcategory) && (
                          <Alert variant="info" className="mb-3 equipment-manager-selected-items-search-results-info">
                              <FontAwesomeIcon icon={faSearch} className="me-2" />
                              תוצאות חיפוש: {filteredSelectedItems.length} פריטים נמצאו
                              {selectedItemsSearchTerm && <span className="ms-2">(חיפוש: "{selectedItemsSearchTerm}")</span>}
                              {selectedItemsSelectedCategory && <span className="ms-2">(קטגוריה: {selectedItemsSelectedCategory})</span>}
                              {selectedItemsSelectedSubcategory && <span className="ms-2">(תת-קטגוריה: {selectedItemsSelectedSubcategory})</span>}
                          </Alert>
                      )}
                  </div>
              )}
              
              {groupedEventItems && Object.keys(groupedEventItems).length === 0 ? (
                <Alert variant="info">לא שויכו פריטים לאירוע זה עדיין.</Alert>
              ) : (
                <>
                  {/* הודעת "אין תוצאות" אם יש סינון פעיל */}
                  {(selectedItemsSearchTerm || selectedItemsSelectedCategory || selectedItemsSelectedSubcategory) && 
                   Object.keys(filteredGroupedSelectedItems).length === 0 && (
                    <Alert variant="warning">
                      <FontAwesomeIcon icon={faSearch} className="me-2" />
                      לא נמצאו פריטים התואמים לחיפוש שלך. נסה לשנות את תנאי החיפוש.
                    </Alert>
                  )}
                  
                  <Table striped bordered hover responsive size="sm" className="table-to-print">
                    <thead>
                      <tr>
                        <th>פריט</th>
                        <th style={{width: '100px'}}>כמות</th>
                        <th>קטגוריה</th>
                        <th>תת-קטגוריה</th>
                        {!isTechnician && <th>הערות לציוד</th>}
                        <th className="d-print-none">סוג</th>
                        <th className="d-print-none">פעולות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries((selectedItemsSearchTerm || selectedItemsSelectedCategory || selectedItemsSelectedSubcategory) ? filteredGroupedSelectedItems : groupedEventItems).map(([category, subcategories]) => (
                      <React.Fragment key={category}>
                                        <tr className="table-dark">
                  <td colSpan={isTechnician ? 6 : 7}><strong>{category}</strong></td>
                </tr>
                        {Object.entries(subcategories).map(([subcategory, items]) => (
                          <React.Fragment key={subcategory}>
                                                                            <tr className="table-secondary subcategory-header">
                                                    <td colSpan={isTechnician ? 6 : 7} className="ps-4"><strong>{subcategory}</strong></td>
                                                </tr>
                            {items.map(item => {
                              if ('equipment_name' in item) {
                                const inventoryItem = allInventoryEquipment.find(invEq => invEq.id === item.equipment);
                                const isHeavy = inventoryItem?.is_heavy;
                                const casesLabel = formatCasesLabel(item.quantity_needed, inventoryItem?.case_quantity);
                                return (
                                  <React.Fragment key={`eq-${item.id}`}>
                                    <tr className={item.is_rented_item ? 'table-info' : 'table-primary'}>
                                      <td 
                                        onClick={() => !item.is_rented_item ? handleEquipmentRowClick(item) : undefined}
                                        style={{ cursor: !item.is_rented_item ? 'pointer' : 'default' }}
                                        title={!item.is_rented_item ? 'לחץ לצפייה בפרטי הציוד' : undefined}
                                      >
                                        {item.equipment_name || item.rented_item_name}
                                        {isHeavy && <span className="text-danger ms-2" title="ציוד כבד">(כבד)</span>}
                                        {item.last_modified_after_event && (
                                          <span className="text-warning ms-2" title={`נערך לאחר תאריך האירוע ב-${new Date(item.last_modified_after_event).toLocaleDateString('he-IL')}`}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} /> (נערך לאחר האירוע)
                                          </span>
                                        )}
                                      </td>
                                      <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            value={item.quantity_needed}
                                            onChange={(e) => handleQuantityChange(item.id as number, e.target.value, 'equipment')}
                                            onKeyDown={(e) => handleQuantityKeyDown(e, item.id as number, item.quantity_needed, 'equipment')}
                                            onKeyPress={(e) => {
                                              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                e.preventDefault();
                                                handleQuantityKeyDown(e as any, item.id as number, item.quantity_needed, 'equipment');
                                              }
                                            }}
                                            min="1"
                                            className="d-print-none"
                                        />
                                        <span className="d-none d-print-inline">{item.quantity_needed}</span>
                                        {casesLabel && (
                                          <small className="d-block text-muted">
                                            ({casesLabel})
                                          </small>
                                        )}
                                      </td>
                                      <td>{item.category_name || item.rented_item_category || '-'}</td>
                                      <td>{item.subcategory_name || item.rented_item_subcategory || '-'}</td>
                                      {!isTechnician && (
                                        <td>
                                          <Form.Control
                                            as="textarea"
                                            rows={2}
                                            size="sm"
                                            className="d-print-none"
                                            placeholder="כתוב הערה על הפריט..."
                                            value={equipmentNotesDraft[item.id as number] ?? item.notes ?? ''}
                                            onChange={(e) => handleEquipmentNoteDraftChange(item.id as number, e.target.value)}
                                            onBlur={() => handleEquipmentNoteBlur(item.id as number)}
                                          />
                                          <div className="d-none d-print-block">
                                            {item.notes || '-'}
                                          </div>
                                          {canViewNoteAuthor && item.note_updated_by_name && (
                                            <small className="text-muted d-block mt-1">
                                              נרשם על ידי: {item.note_updated_by_name}
                                            </small>
                                          )}
                                        </td>
                                      )}
                                      <td className="d-print-none">{item.is_rented_item ? 'מושכר' : 'ציוד'}</td>
                                      <td className="d-print-none">
                                        <Button variant="danger" size="sm" onClick={() => handleRemoveItem(item.id! as number, 'equipment')}>
          <FontAwesomeIcon icon={faTrash} className="me-1" />
          הסר
        </Button>
                                      </td>
                                    </tr>
                                    {item.is_rented_item && (
                                      <tr className="table-secondary">
                                        <td colSpan={isTechnician ? 6 : 7}>
                                          <div className="d-flex justify-content-start align-items-center flex-wrap px-4">
                                            <span className="me-4"><strong>ספק:</strong> {item.rental_supplier_name || 'אין'}</span>
                                            <span className="me-4">
                                              <strong>תאריך הגעה:</strong> 
                                              <input
                                                type="date"
                                                className="form-control form-control-sm d-inline-block ms-2"
                                                style={{ width: 'auto' }}
                                                value={item.rented_arrival_date || ''}
                                                onChange={(e) => handleRentedDateChange(item.id as number, 'arrival', e.target.value)}
                                              />
                                            </span>
                                            <span className="me-4">
                                              <strong>תאריך עזיבה:</strong> 
                                              <input
                                                type="date"
                                                className="form-control form-control-sm d-inline-block ms-2"
                                                style={{ width: 'auto' }}
                                                value={item.rented_departure_date || ''}
                                                onChange={(e) => handleRentedDateChange(item.id as number, 'departure', e.target.value)}
                                              />
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              } else if ('consumable_name' in item) {
                                return (
                                  <tr key={`cn-${item.id}`} className="table-success">
                                    <td>{item.consumable_name}</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            value={item.quantity_used}
                                            onChange={(e) => handleQuantityChange(item.id as number, e.target.value, 'consumable')}
                                            min="0"
                                            className="d-print-none"
                                        />
                                        <span className="d-none d-print-inline">{item.quantity_used} {item.consumable_unit}</span>
                                    </td>
                                    <td>מתכלה</td>
                                    <td>-</td>
                                    {!isTechnician && <td>-</td>}
                                    <td className="d-print-none"><span className="badge bg-success">מתכלה</span></td>
                                    <td className="d-print-none">
                                      <Button variant="danger" size="sm" onClick={() => handleRemoveItem(item.id! as number, 'consumable')}>
          <FontAwesomeIcon icon={faTrash} className="me-1" />
          הסר
        </Button>
                                    </td>
                                  </tr>
                                );
                              }
                              return null;
                            })}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
                </>
              )}
            <div className="d-flex justify-content-between mt-3">
                            <Button variant="outline-secondary" onClick={() => window.print()}>
              <FontAwesomeIcon icon={faPrint} className="me-1" />
              הדפס רשימה
            </Button>
            </div>
          </Card.Body>
        </Card>
        </Col>

        <Col lg={5} className="d-print-none">
          <Card>
              <Card.Header>הוספת פריטים</Card.Header>
              <Card.Body>
                  <div className="d-flex justify-content-around mb-3">
                              <Button size="sm" variant={addMode === 'internal' ? 'primary' : 'outline-primary'} onClick={() => {
                                  setAddMode('internal');
                                  clearFilters();
                              }}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          מהמלאי
        </Button>
        <Button size="sm" variant={addMode === 'external' ? 'info' : 'outline-info'} onClick={() => {
                                  setAddMode('external');
                                  clearFilters();
                              }}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          מושכר חיצוני
        </Button>
        <Button size="sm" variant={addMode === 'consumable' ? 'success' : 'outline-success'} onClick={() => {
                                  setAddMode('consumable');
                                  clearFilters();
                              }}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          מתכלים
        </Button>
                  </div>
                  
                  {addMode === 'internal' && (
                      <div>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">הוסף מהמלאי</h5>
                            <Button size="sm" variant="outline-success" onClick={openAddNewEquipmentModal}>
                              <FontAwesomeIcon icon={faPlus} className="me-1" />
                              הוספת ציוד חדש
                            </Button>
                          </div>
                          
                          {/* חיפוש ציוד מהמלאי */}
                          <div className="mb-3 equipment-manager-search-container">
                              <InputGroup className="mb-2 equipment-manager-search">
                                  <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                                  <Form.Control
                                      className="equipment-manager-search-input"
                                      placeholder="חפש ציוד מהמלאי..."
                                      value={searchTerm}
                                      onChange={(e) => setSearchTerm(e.target.value)}
                                  />
                                  <Button 
                                      className="equipment-manager-filter-button"
                                      variant="outline-secondary" 
                                      onClick={() => setShowSearchFilters(!showSearchFilters)}
                                  >
                                      <FontAwesomeIcon icon={faFilter} />
                                  </Button>
                              </InputGroup>

                              {showSearchFilters && (
                                  <div className="mb-3 equipment-manager-filters-container">
                                      <Row>
                                          <Col md={6}>
                                              <Form.Label>קטגוריה</Form.Label>
                                              <Form.Select 
                                                  className="equipment-manager-category-select"
                                                  value={selectedCategory} 
                                                  onChange={(e) => setSelectedCategory(e.target.value)}
                                              >
                                                  <option value="">כל הקטגוריות</option>
                                                  {sortedCategories.map(cat => (
                                                      <option key={cat} value={cat}>{cat}</option>
                                                  ))}
                                              </Form.Select>
                                          </Col>
                                          <Col md={6}>
                                              <Form.Label>תת-קטגוריה</Form.Label>
                                              <Form.Select 
                                                  className="equipment-manager-subcategory-select"
                                                  value={selectedSubcategory} 
                                                  onChange={(e) => setSelectedSubcategory(e.target.value)}
                                                  disabled={!selectedCategory}
                                              >
                                                  <option value="">כל התת-קטגוריות</option>
                                                  {availableSubcategories.map(sub => (
                                                      <option key={sub} value={sub}>{sub}</option>
                                                  ))}
                                              </Form.Select>
                                          </Col>
                                      </Row>
                                      <div className="d-flex gap-2 mt-2">
                                          <Button 
                                              className="equipment-manager-clear-filters-button"
                                              variant="secondary" 
                                              size="sm" 
                                              onClick={clearFilters}
                                          >
                                              <FontAwesomeIcon icon={faTimes} /> נקה סינונים
                                          </Button>
                                          <Button variant="outline-secondary" size="sm" onClick={() => setShowSearchFilters(false)}>
                                              <FontAwesomeIcon icon={faTimes} /> סגור סינונים
                                          </Button>
                                      </div>
                                  </div>
                              )}

                              {/* הודעת תוצאות החיפוש */}
                              {(searchTerm || selectedCategory || selectedSubcategory) && (
                                  <Alert variant="info" className="mb-3 equipment-manager-search-results-info">
                                      <FontAwesomeIcon icon={faSearch} className="me-2" />
                                      תוצאות חיפוש: {filteredInventoryEquipment.length} פריטים נמצאו
                                      {searchTerm && <span className="ms-2">(חיפוש: "{searchTerm}")</span>}
                                      {selectedCategory && <span className="ms-2">(קטגוריה: {selectedCategory})</span>}
                                      {selectedSubcategory && <span className="ms-2">(תת-קטגוריה: {selectedSubcategory})</span>}
                                  </Alert>
                              )}
                          </div>

                          <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px' }}>
                              {Object.keys(filteredGroupedInventory).length > 0 ? (
                                  <Accordion alwaysOpen>
                                      {Object.entries(filteredGroupedInventory).map(([category, subcategories], catIndex) => (
                                          <Accordion.Item eventKey={String(catIndex)} key={category}>
                                              <Accordion.Header>{category}</Accordion.Header>
                                              <Accordion.Body>
                                                  {Object.entries(subcategories).map(([subcategory, items]) => (
                                                      <div key={subcategory}>
                                                          <strong>{subcategory}</strong>
                                                          <Table striped borderless hover size="sm" responsive className="mt-1">
                                                              <tbody>
                                                                  {items.map(item => {
                                                                      const selectedQuantity = Number(quantitiesToAdd[item.id!] || 0);
                                                                      const availableQuantity = availableEquipmentMap.get(item.id!) || 0;
                                                                      const isSelected = selectedQuantity > 0;
                                                                      
                                                                      return (
                                                                          <tr key={item.id} className={(availableQuantity <= 0) ? 'text-muted' : (isSelected ? 'table-success' : '')}>
                                                                              <td 
                                                                                onClick={() => handleEquipmentRowClick({ equipment: item.id, is_rented_item: false } as EventEquipmentItem)}
                                                                                style={{ cursor: 'pointer' }}
                                                                                title="לחץ לצפייה בפרטי הציוד"
                                                                              >
                                                                                  <span style={{ color: (availableQuantity > 0) ? 'green' : 'red' }}>●</span> {item.name}
                                                                                  <small className="text-muted ms-2">({availableQuantity} זמין)</small>
                                                                                  {isSelected && (
                                                                                      <span className="badge bg-success ms-2">
                                                                                          נבחר: {selectedQuantity}
                                                                                      </span>
                                                                                  )}
                                                                              </td>
                                                                              <td style={{ width: '120px' }}>
                                                                                  <div className="d-flex align-items-center">
                                                                                      <Button variant="outline-secondary" size="sm" onClick={() => handleDecrementAddQuantity(item.id)}>
                                                                                          <FontAwesomeIcon icon={faMinus} className="me-1" />
                                                                                          -
                                                                                      </Button>
                                                                                      <Form.Control 
                                                                                          type="number"
                                                                                          size="sm"
                                                                                          placeholder="0"
                                                                                          value={quantitiesToAdd[item.id!] || ''}
                                                                                          onChange={e => handleAddQuantityChange(item.id, e.target.value)}
                                                                                          disabled={!availableEquipmentMap.has(item.id!) || (availableEquipmentMap.get(item.id!) || 0) <= 0}
                                                                                          min="0"
                                                                                          className="mx-1"
                                                                                          style={{ 
                                                                                              backgroundColor: isSelected ? '#d4edda' : 'white',
                                                                                              borderColor: isSelected ? '#28a745' : '#ced4da'
                                                                                          }}
                                                                                      />
                                                                                      <Button variant="outline-secondary" size="sm" onClick={() => handleIncrementAddQuantity(item.id)}>
                                                                                          <FontAwesomeIcon icon={faPlus} className="me-1" />
                                                                                          +
                                                                                      </Button>
                                                                                  </div>
                                                                              </td>
                                                                          </tr>
                                                                      );
                                                                  })}
                                                              </tbody>
                                                          </Table>
                                                      </div>
                                                  ))}
                                              </Accordion.Body>
                                          </Accordion.Item>
                                      ))}
                                  </Accordion>
                              ) : (
                                  (searchTerm || selectedCategory || selectedSubcategory) ? (
                                      <Alert variant="warning">
                                          <FontAwesomeIcon icon={faSearch} className="me-2" />
                                          לא נמצאו פריטים התואמים לחיפוש שלך. נסה לשנות את תנאי החיפוש.
                                      </Alert>
                                  ) : (
                                      <Alert variant="info">אין ציוד זמין במלאי.</Alert>
                                  )
                              )}
                          </div>
                          <div className="d-grid gap-2 mt-3">
                              <Button onClick={handleAddMultipleInternalItems}>
                                <FontAwesomeIcon icon={faCheckSquare} className="me-1" />
                                אישור ציוד מסומן
                              </Button>
                          </div>
                      </div>
                  )}

                  {addMode === 'external' && (
                      <div>
                          <h5>הוסף ציוד מושכר</h5>
                          
                          {/* חיפוש ציוד מושכר קיים */}
                          <div className="mb-3 equipment-manager-search-container">
                              <InputGroup className="equipment-manager-search">
                                  <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                                  <Form.Control
                                      className="equipment-manager-search-input"
                                      placeholder="חפש ציוד מושכר קיים..."
                                      value={searchTerm}
                                      onChange={(e) => setSearchTerm(e.target.value)}
                                  />
                              </InputGroup>

                              {/* הודעת תוצאות החיפוש */}
                              {searchTerm && (
                                  <Alert variant="info" className="mt-2 equipment-manager-search-results-info">
                                      <FontAwesomeIcon icon={faSearch} className="me-2" />
                                      חיפוש: "{searchTerm}"
                                  </Alert>
                              )}
                          </div>

                           <Form.Group className="mb-2">
                              <Form.Label>שם הפריט</Form.Label>
                              <Form.Control type="text" name="rented_item_name" value={rentedItemFormData.rented_item_name} onChange={handleRentedItemChange} />
                          </Form.Group>
                          <Form.Group className="mb-2">
                              <Form.Label>ספק</Form.Label>
                              <Form.Control type="text" name="rental_supplier_name" value={rentedItemFormData.rental_supplier_name} onChange={handleRentedItemChange} />
                          </Form.Group>
                          <Form.Group className="mb-2">
                              <Form.Label>קטגוריה</Form.Label>
                              <Form.Select name="rented_item_category_id" value={rentedItemFormData.rented_item_category_id} onChange={handleRentedItemChange}>
                                   <option value="">בחר...</option>
                                   {allCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.category_name}</option>)}
                              </Form.Select>
                          </Form.Group>
                          <Form.Group className="mb-2">
                              <Form.Label>תת-קטגוריה</Form.Label>
                              <Form.Select name="rented_item_subcategory_id" value={rentedItemFormData.rented_item_subcategory_id} onChange={handleRentedItemChange} disabled={filteredSubcategories.length === 0}>
                                   <option value="">בחר...</option>
                                   {filteredSubcategories.map(sub => <option key={sub.id} value={sub.id}>{sub.subcategory_name}</option>)}
                              </Form.Select>
                          </Form.Group>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label>תאריך הגעה</Form.Label>
                                <Form.Control 
                                  type="date" 
                                  name="rented_arrival_date" 
                                  value={rentedItemFormData.rented_arrival_date} 
                                  onChange={handleRentedItemChange} 
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label>תאריך עזיבה</Form.Label>
                                <Form.Control 
                                  type="date" 
                                  name="rented_departure_date" 
                                  value={rentedItemFormData.rented_departure_date} 
                                  onChange={handleRentedItemChange} 
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Form.Group className="mb-2">
                              <Form.Label>כמות</Form.Label>
                              <Form.Control type="number" name="quantity" value={rentedItemFormData.quantity} onChange={e => setRentedItemFormData({...rentedItemFormData, quantity: parseInt(e.target.value)})} min="1" />
                          </Form.Group>
                          <Button className="w-100 mt-2" variant="info" onClick={handleAddExternalEquipment}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          הוסף פריט מושכר
        </Button>
                      </div>
                  )}

                  {addMode === 'consumable' && (
                      <div>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">הוסף מתכלים</h5>
                            <Button size="sm" variant="outline-success" onClick={openAddNewConsumableModal}>
                              <FontAwesomeIcon icon={faPlus} className="me-1" />
                              הוסף פריט מתכלה חדש
                            </Button>
                          </div>
                          
                          {/* חיפוש מתכלים */}
                          <div className="mb-3 equipment-manager-search-container">
                              <InputGroup className="equipment-manager-search">
                                  <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                                  <Form.Control
                                      className="equipment-manager-search-input"
                                      placeholder="חפש מתכלים..."
                                      value={searchTerm}
                                      onChange={(e) => setSearchTerm(e.target.value)}
                                  />
                              </InputGroup>

                              {/* הודעת תוצאות החיפוש */}
                              {searchTerm && (
                                  <Alert variant="info" className="mt-2 equipment-manager-search-results-info">
                                      <FontAwesomeIcon icon={faSearch} className="me-2" />
                                      תוצאות חיפוש: {filteredConsumables.length} פריטים נמצאו
                                      <span className="ms-2">(חיפוש: "{searchTerm}")</span>
                                  </Alert>
                              )}
                          </div>

                                                      <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px' }}>
                              {filteredConsumables.length > 0 ? (
                                  <Table striped borderless hover size="sm" responsive>
                                      <tbody>
                                          {filteredConsumables.map(item => (
                                              <tr key={item.id}>
                                                  <td>{item.name} <small className="text-muted">({item.unit})</small></td>
                                                  <td style={{ width: '80px' }}>
                                                      <Form.Control 
                                                          type="number"
                                                          size="sm"
                                                          placeholder="0"
                                                          value={consumableQuantities[item.id!] || ''}
                                                          onChange={e => setConsumableQuantities({...consumableQuantities, [item.id!]: e.target.value})}
                                                      />
                                                  </td>
                                              </tr>
                                          ))}
                                      </tbody>
                                  </Table>
                              ) : (
                                  searchTerm ? (
                                      <Alert variant="warning">
                                          <FontAwesomeIcon icon={faSearch} className="me-2" />
                                          לא נמצאו מתכלים התואמים לחיפוש שלך. נסה לשנות את מילת החיפוש.
                                      </Alert>
                                  ) : (
                                      <Alert variant="info">אין מתכלים זמינים.</Alert>
                                  )
                              )}
                          </div>
                           <Button className="w-100 mt-2" variant="success" onClick={handleSaveConsumables}>
          <FontAwesomeIcon icon={faSave} className="me-1" />
          שמור מתכלים
        </Button>
                      </div>
                  )}


              </Card.Body>
          </Card>
          
          <Card className="mt-3">
              <Card.Header>פעולות נוספות</Card.Header>
              <Card.Body>
                  {/* המלצות אוטומטיות */}
                  <div className="mb-3">
                      <h6>המלצות אוטומטיות</h6>
                      <Button size="sm" variant="outline-primary" onClick={handleRecommend} className="w-100">
                          <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                          הצג המלצות
                      </Button>
                      
                      {recommendedItems.length > 0 && (
                          <div className="mt-3">
                              <InputGroup className="mb-2">
                                  <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                                  <Form.Control
                                      placeholder="חפש בהמלצות לפי שם פריט או מקור המלצה..."
                                      value={recommendedItemsSearchTerm}
                                      onChange={e => setRecommendedItemsSearchTerm(e.target.value)}
                                  />
                                  <InputGroup.Text><FontAwesomeIcon icon={faFilter} /></InputGroup.Text>
                                  <Form.Select
                                      value={recommendedItemsTypeFilter}
                                      onChange={e => setRecommendedItemsTypeFilter(e.target.value)}
                                      style={{ maxWidth: '170px' }}
                                  >
                                      <option value="">כל הסוגים</option>
                                      <option value="equipment">ציוד</option>
                                      <option value="consumable">מתכלים</option>
                                  </Form.Select>
                                  <Form.Select
                                      value={recommendedItemsSourceFilter}
                                      onChange={e => setRecommendedItemsSourceFilter(e.target.value)}
                                      style={{ maxWidth: '180px' }}
                                  >
                                      <option value="">כל המקורות</option>
                                      <option value="location">מיקום</option>
                                      <option value="client">לקוח</option>
                                      <option value="complementary">משלים לציוד (ציוד/מתכלים)</option>
                                      <option value="long_event">אירוע ארוך</option>
                                      <option value="event_internal">אירוע פנימי</option>
                                      <option value="event_external">אירוע חיצוני</option>
                                      <option value="other">אחר</option>
                                  </Form.Select>
                                  <Button
                                      variant="outline-secondary"
                                      onClick={() => {
                                        setRecommendedItemsSearchTerm('');
                                        setRecommendedItemsTypeFilter('');
                                        setRecommendedItemsSourceFilter('');
                                      }}
                                  >
                                      נקה
                                  </Button>
                              </InputGroup>

                              {(recommendedItemsSearchTerm || recommendedItemsTypeFilter || recommendedItemsSourceFilter) && (
                                  <Alert variant="info" className="mb-2">
                                      תוצאות סינון: {filteredRecommendedItems.length} מתוך {recommendedItems.length}
                                  </Alert>
                              )}

                              <Table striped bordered hover size="sm" responsive>
                                  <thead>
                                      <tr>
                                          <th>
                                              <Form.Check
                                                  type="checkbox"
                                                  checked={areAllFilteredRecommendationsSelected}
                                                  onChange={e => setRecommendedItems(
                                                    recommendedItems.map(i => {
                                                      const matchesSearch = !recommendedItemsSearchTerm ||
                                                        i.name.toLowerCase().includes(recommendedItemsSearchTerm.toLowerCase()) ||
                                                        i.reason.toLowerCase().includes(recommendedItemsSearchTerm.toLowerCase());
                                                      const matchesType = !recommendedItemsTypeFilter || i.type === recommendedItemsTypeFilter;
                                                      const matchesSource =
                                                        !recommendedItemsSourceFilter ||
                                                        recommendationReasonMatchesSource(i.reason, recommendedItemsSourceFilter);
                                                      return (matchesSearch && matchesType && matchesSource) ? { ...i, isSelected: e.target.checked } : i;
                                                    })
                                                  )}
                                              />
                                          </th>
                                          <th>פריט</th>
                                          <th>כמות</th>
                                          <th>המלצה מבוססת על</th>
                                      </tr>
                                  </thead>
                                  <tbody>
                                      {filteredRecommendedItems.map(item => (
                                          <tr key={item.dedupeKey}>
                                              <td><Form.Check type="checkbox" checked={item.isSelected} onChange={e => setRecommendedItems(recommendedItems.map(i => i.dedupeKey === item.dedupeKey ? {...i, isSelected: e.target.checked} : i))} /></td>
                                              <td>{item.name}{item.type === 'consumable' ? <span className="text-muted small ms-1">(מתכלה)</span> : null}</td>
                                              <td>{item.quantity}</td>
                                              <td>{item.reason}</td>
                                          </tr>
                                      ))}
                                  </tbody>
                              </Table>
                              {filteredRecommendedItems.length === 0 && (
                                  <Alert variant="warning" className="mt-2 mb-0">
                                      לא נמצאו המלצות שמתאימות לחיפוש/סינון שבחרת.
                                  </Alert>
                              )}
                              <div className="d-flex justify-content-between mt-2">
                                  <Button variant="outline-danger" size="sm" onClick={() => handleRecommendSelection('reject_all')}>
                                      <FontAwesomeIcon icon={faBan} className="me-1" />
                                      דחה הכל
                                  </Button>
                                  <Button variant="outline-success" size="sm" onClick={() => handleRecommendSelection('accept_all')}>
                                      <FontAwesomeIcon icon={faCheck} className="me-1" />
                                      קבל הכל
                                  </Button>
                                  <Button size="sm" onClick={() => handleRecommendSelection('accept_selected')}>
                                      <FontAwesomeIcon icon={faCheck} className="me-1" />
                                      קבל את מה שסומן
                                  </Button>
                              </div>
                          </div>
                      )}
                  </div>

                  {/* העתקה מאירוע קיים */}
                  <div className="mb-3">
                      <h6>העתקה מאירוע קיים</h6>
                      <Button size="sm" variant="outline-secondary" onClick={() => setShowCopyFromEventModal(true)} className="w-100">
                          <FontAwesomeIcon icon={faCopy} className="me-1" />
                          העתק מאירוע קיים
                      </Button>
                  </div>

                  {/* טעינת מפרט קיים */}
                  <div className="mb-3">
                      <h6>טעינת מפרט קיים</h6>
                      <Form.Group className="mb-2">
                          <Form.Label>בחר מפרט</Form.Label>
                          <Form.Select value={selectedSpecificationToLoad} onChange={e => setSelectedSpecificationToLoad(parseInt(e.target.value) || '')}>
                              <option value="">בחר...</option>
                              {allSpecifications.map(spec => <option key={spec.id} value={spec.id}>{spec.name}</option>)}
                          </Form.Select>
                      </Form.Group>
                      <Button size="sm" onClick={handleLoadSpecification} disabled={!selectedSpecificationToLoad} className="w-100">
                          <FontAwesomeIcon icon={faSync} className="me-1" />
                          טען / עדכן פריטים ממפרט
                      </Button>
                  </div>
              </Card.Body>
          </Card>
      </Col>
    </Row>

    <Modal show={showCopyFromEventModal} onHide={() => setShowCopyFromEventModal(false)} centered size="lg">
        <Modal.Header closeButton>
            <Modal.Title>העתק ציוד ומתכלים מאירוע אחר</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group className="mb-3">
                <Form.Label>חיפוש אירוע</Form.Label>
                <Form.Control
                    type="text"
                    placeholder="חפש לפי שם, לקוח, תאריך..."
                    value={copyEventSearchQuery}
                    onChange={(e) => setCopyEventSearchQuery(e.target.value)}
                />
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>בחר אירוע להעתקה</Form.Label>
                <Form.Select value={selectedEventToCopyFrom} onChange={e => handleSelectEventToCopyFrom(parseInt(e.target.value) || '')}>
                    <option value="">בחר אירוע...</option>
                    {eventsToCopyFrom.map(e => (
                        <option key={e.id} value={e.id}>
                            {e.event_name} ({e.client_name}) - {moment(e.start_date).format('DD/MM/YYYY')}
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>

            {copiedItemsForSelection.length > 0 && (
                <div className="mt-4">
                    <h5>פריטים מהאירוע הנבחר:</h5>
                    <Table striped bordered hover size="sm" responsive>
                        <thead>
                            <tr>
                                <th><Form.Check type="checkbox" checked={copiedItemsForSelection.every(i => i.isSelected)} onChange={e => setCopiedItemsForSelection(copiedItemsForSelection.map(i => ({...i, isSelected: e.target.checked})))} /></th>
                                <th>פריט</th>
                                <th>כמות</th>
                                <th>סוג</th>
                            </tr>
                        </thead>
                        <tbody>
                            {copiedItemsForSelection.map((item) => (
                                <tr key={item.dedupeKey}>
                                    <td><Form.Check type="checkbox" checked={item.isSelected} onChange={e => setCopiedItemsForSelection(copiedItemsForSelection.map(i => i.dedupeKey === item.dedupeKey ? {...i, isSelected: e.target.checked} : i))} /></td>
                                    <td>{item.name}</td>
                                    <td>{item.quantity}</td>
                                    <td>{item.type === 'equipment' ? 'ציוד' : 'מתכלה'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <div className="d-flex justify-content-between mt-3">
                                <Button variant="outline-danger" onClick={() => handleCopyItemsSelection('reject_all')}>
          <FontAwesomeIcon icon={faBan} className="me-1" />
          דחה הכל
        </Button>
        <Button variant="outline-success" onClick={() => handleCopyItemsSelection('accept_all')}>
          <FontAwesomeIcon icon={faCheck} className="me-1" />
          קבל הכל
        </Button>
                        <Button onClick={() => handleCopyItemsSelection('accept_selected')}>
          <FontAwesomeIcon icon={faCheck} className="me-1" />
          קבל את מה שסומן
        </Button>
                    </div>
                </div>
            )}
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCopyFromEventModal(false)}>
          <FontAwesomeIcon icon={faTimes} className="me-1" />
          ביטול
        </Button>
        </Modal.Footer>
    </Modal>

    {/* מודל: הוספת פריט מתכלה חדש */}
    <Modal show={showAddNewConsumableModal} onHide={() => setShowAddNewConsumableModal(false)} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>הוספת פריט מתכלה חדש</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ConsumableForm
          consumableToEdit={null}
          onCancel={() => setShowAddNewConsumableModal(false)}
          onSuccess={async () => {
            setShowAddNewConsumableModal(false);
            showMessage('הצלחה', 'פריט מתכלה חדש נוסף למלאי.', 'success');
            await fetchAllDependencies();
          }}
        />
      </Modal.Body>
    </Modal>

    {/* מודל לבחירת פריטים מהמפרט */}
    <Modal show={showSpecificationSelectionModal} onHide={() => setShowSpecificationSelectionModal(false)} centered size="lg">
        <Modal.Header closeButton>
            <Modal.Title>בחר פריטים מהמפרט</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {specificationItemsForSelection.length > 0 && (
                <div>
                    <h6>פריטים מהמפרט הנבחר:</h6>
                    <Table striped bordered hover size="sm" responsive>
                        <thead>
                            <tr>
                                <th><Form.Check type="checkbox" checked={specificationItemsForSelection.every(i => i.isSelected)} onChange={e => setSpecificationItemsForSelection(specificationItemsForSelection.map(i => ({...i, isSelected: e.target.checked})))} /></th>
                                <th>פריט</th>
                                <th>כמות</th>
                                <th>סוג</th>
                                <th>מקור</th>
                            </tr>
                        </thead>
                        <tbody>
                            {specificationItemsForSelection.map((item) => (
                                <tr key={item.dedupeKey}>
                                    <td><Form.Check type="checkbox" checked={item.isSelected} onChange={e => setSpecificationItemsForSelection(specificationItemsForSelection.map(i => i.dedupeKey === item.dedupeKey ? {...i, isSelected: e.target.checked} : i))} /></td>
                                    <td>{item.name}</td>
                                    <td>{item.quantity}</td>
                                    <td>{item.type === 'equipment' ? 'ציוד' : 'מתכלה'}</td>
                                    <td>{item.reason}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <div className="d-flex justify-content-between mt-3">
                        <Button variant="outline-danger" onClick={() => handleSpecificationSelection('reject_all')}>
                            <FontAwesomeIcon icon={faBan} className="me-1" />
                            דחה הכל
                        </Button>
                        <Button variant="outline-success" onClick={() => handleSpecificationSelection('accept_all')}>
                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                            קבל הכל
                        </Button>
                        <Button onClick={() => handleSpecificationSelection('accept_selected')}>
                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                            קבל את מה שסומן
                        </Button>
                    </div>
                </div>
            )}
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSpecificationSelectionModal(false)}>
                <FontAwesomeIcon icon={faTimes} className="me-1" />
                ביטול
            </Button>
        </Modal.Footer>
    </Modal>

    {/* מודל: הוספת ציוד חדש למלאי */}
    <Modal show={showAddNewEquipmentModal} onHide={() => setShowAddNewEquipmentModal(false)} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>הוספת ציוד חדש למלאי</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <EquipmentForm
          equipmentToEdit={null}
          onCancel={() => setShowAddNewEquipmentModal(false)}
          onSuccess={async () => {
            setShowAddNewEquipmentModal(false);
            showMessage('הצלחה', 'ציוד חדש נוסף למלאי.', 'success');
            await fetchAllDependencies();
            await fetchAvailableEquipment();
          }}
        />
      </Modal.Body>
    </Modal>
        </>
      ) : (
        <Card className="mb-4">
          <Card.Body className="text-center">
            <div className="py-5">
              <FontAwesomeIcon icon={faList} size="3x" className="text-muted mb-3" />
              <h4>בחר אירוע כדי להתחיל בניהול הציוד</h4>
              <p className="text-muted mb-4">
                בחר אירוע מהרשימה למעלה כדי להציג ולנהל את הציוד שלו.<br/>
                ניתן לנהל ציוד לאירועים בכל הסטטוסים כולל אירועים שהסתיימו או בוטלו.
              </p>
              <div className="d-flex justify-content-center gap-2 flex-wrap">
                <Button 
                  variant="outline-primary" 
                  onClick={() => setShowEventFilters(true)}
                  disabled={loadingEvents || !!errorEvents}
                >
                  <FontAwesomeIcon icon={faFilter} className="me-1" />
                  פתח סינון וחיפוש
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={onBack}
                >
                  <FontAwesomeIcon icon={faList} className="me-1" />
                  רשימת אירועים
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

    <MessageModal show={showMessageModal} onHide={() => setShowMessageModal(false)} title={messageModalTitle} message={messageModalContent} variant={messageModalVariant} />
    <ConfirmationModal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} title={confirmModalTitle} message={confirmModalMessage} onConfirm={confirmAction || (() => {})} />
    
    {/* מודל פרטי ציוד */}
    <EquipmentDetailModal 
      show={showEquipmentDetailModal} 
      onHide={() => setShowEquipmentDetailModal(false)} 
      equipment={selectedEquipmentForDetails} 
      allEquipment={allInventoryEquipment} 
    />
  </Container>
);
};

export default EventEquipmentManager;
